import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  MessageSquare, Send, FileText, Users, BarChart3, Settings, Plus, Trash2,
  RefreshCw, Mail, MailOpen, AlertCircle, CheckCircle, Clock, Megaphone,
  Upload, Phone, Search, ArrowUpRight, ArrowDownLeft, Inbox, Rocket,
  ShieldBan, Ban, Reply, Tag, Check, X, MoreHorizontal, ChevronDown, ChevronRight, Users2, ListFilter,
  Pause, Play, Edit, Pencil, Download, RotateCcw, FolderInput
} from "lucide-react";

const formatDate = (d) => {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
};

export default function SmsMarketing({ allowedTabs = null, pageTitle = "SMS Marketing", pageSubtitle = "Envie e receba SMS via DINSTAR Gateway" } = {}) {
  // allowedTabs: array de keys das tabs visiveis. null = todas (default exclui "contatos" e "blacklist" que migraram para Gestao de Telefones)
  const _effectiveAllowed = Array.isArray(allowedTabs) ? allowedTabs : ["enviar","templates","campanhas","caixa","gestao","relatorios"];
  const _tabAllowed = (k) => _effectiveAllowed.includes(k);
  const _isSingleTab = _effectiveAllowed.length === 1;
  const [activeTab, setActiveTab] = useState(_effectiveAllowed[0] || "enviar");
  const [loading, setLoading] = useState(false);

  // Data
  const [config, setConfig] = useState({ gateway_ip: "", gateway_port: "80", default_port: 1, configurado: false });
  const [templates, setTemplates] = useState([]);
  const [campanhas, setCampanhas] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [mensagens, setMensagens] = useState([]);
  const [numerosEnviados, setNumerosEnviados] = useState({});
  const [filtroSmsStatus, setFiltroSmsStatus] = useState("todos");
  const [filtroAcao, setFiltroAcao] = useState("todos"); // todos | sem_acao | so_sms | so_ligacao | ambos
  const [relatorio, setRelatorio] = useState({ total_enviados: 0, total_recebidos: 0, total_erros: 0, taxa_sucesso: 0, campanhas_total: 0, campanhas_enviadas: 0, total_contatos: 0, total_blacklist: 0 });

  // Blacklist
  const [blacklist, setBlacklist] = useState([]);
  const [showNovoBlacklist, setShowNovoBlacklist] = useState(false);
  const [blacklistForm, setBlacklistForm] = useState({ telefone: "", motivo: "" });
  const [searchBlacklist, setSearchBlacklist] = useState("");
  const [uploadingBlacklist, setUploadingBlacklist] = useState(false);

  // Modals
  const [showConfig, setShowConfig] = useState(false);
  const [showNovoTemplate, setShowNovoTemplate] = useState(false);
  const [showNovaCampanha, setShowNovaCampanha] = useState(false);

  const [showNovoContato, setShowNovoContato] = useState(false);
  const [showImportarContatos, setShowImportarContatos] = useState(false);
  const [importingContatos, setImportingContatos] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [chipsHealth, setChipsHealth] = useState({});  // chip_id -> health data

  // Polling de saude dos chips (circuit breaker, cooldown) a cada 10 seg
  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const hRes = await axios.get(`${API}/sms/chips/health`);
        const map = {};
        (hRes.data?.chips || []).forEach(h => { map[h.chip_id] = h; });
        setChipsHealth(map);
      } catch(e) { /* silencioso */ }
    };
    fetchHealth(); // imediato ao montar (evita "--" inicial)
    const interval = setInterval(fetchHealth, 10000);
    return () => clearInterval(interval);
  }, []);

  // Helper: forcar recovery de chip em cooldown
  const recuperarChip = async (chipId) => {
    try {
      await axios.post(`${API}/sms/chips/${chipId}/recover`);
      toast.success("Chip recuperado do cooldown");
      const hRes = await axios.get(`${API}/sms/chips/health`);
      const map = {};
      (hRes.data?.chips || []).forEach(h => { map[h.chip_id] = h; });
      setChipsHealth(map);
    } catch(e) { toast.error("Erro ao recuperar chip"); }
  };

  // Helper: formatar tempo restante de cooldown
  const fmtCooldown = (segs) => {
    if (segs <= 0) return "";
    if (segs < 60) return `${segs}s`;
    if (segs < 3600) return `${Math.ceil(segs/60)}min`;
    return `${(segs/3600).toFixed(1)}h`;
  };

  // Forms
  const [configForm, setConfigForm] = useState({ gateway_ip: "", gateway_port: "80", default_port: 1 });
  const [templateForm, setTemplateForm] = useState({ nome: "", categoria: "FGTS", texto: "" });
  const [campanhaForm, setCampanhaForm] = useState({ nome: "", template_id: "", texto: "", grupo_contato: "", tipo_telefone: "todos", whatsapp_status: "todos", sms_status: "todos", acao: "todos" });
  const [campanhaPreview, setCampanhaPreview] = useState({ total_no_grupo: 0, total_apos_filtros: 0, total_validos: 0 });
  // Preview dos filtros da Nova Campanha (apos campanhaForm/Preview state)
  useEffect(() => {
    if (!showNovaCampanha || !campanhaForm.grupo_contato) {
      setCampanhaPreview({ total_no_grupo: 0, total_apos_filtros: 0, total_validos: 0 });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const grupo = campanhaForm.grupo_contato === " " ? "" : campanhaForm.grupo_contato;
        const res = await axios.post(`${API}/sms/contatos/preview-filtro`, {
          grupo_contato: grupo,
          tipo_telefone: campanhaForm.tipo_telefone,
          whatsapp_status: campanhaForm.whatsapp_status,
          sms_status: campanhaForm.sms_status,
          acao: campanhaForm.acao,
        });
        setCampanhaPreview(res.data || {});
      } catch (e) {}
    }, 400);
    return () => clearTimeout(t);
  }, [showNovaCampanha, campanhaForm.grupo_contato, campanhaForm.tipo_telefone, campanhaForm.whatsapp_status, campanhaForm.sms_status, campanhaForm.acao]);
  const [campanhaOrigem, setCampanhaOrigem] = useState("grupo");
  const [campanhaArquivo, setCampanhaArquivo] = useState(null);
  const [uploadingCampanha, setUploadingCampanha] = useState(false);
  const [contatoForm, setContatoForm] = useState({ nome: "", telefone: "", grupo: "Geral", tags: [] });
  const [importForm, setImportForm] = useState({ origem: "clientes", grupo: "Clientes", tag: "", tags: [] });

  // Send SMS form
  const [smsNumeros, setSmsNumeros] = useState("");
  const [smsTexto, setSmsTexto] = useState("");
  const [smsTemplateId, setSmsTemplateId] = useState("");
  const [enviando, setEnviando] = useState(false);

  // Search/filter
  const [searchContato, setSearchContato] = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState("todos");
  const [filtroContatoTag, setFiltroContatoTag] = useState("todos");
  const [tagDropdownAberto, setTagDropdownAberto] = useState(null);
  const [filtroMsgTipo, setFiltroMsgTipo] = useState("todos");
  const [filtroVerificado, setFiltroVerificado] = useState("todos"); // todos, verificado, pendente
  const [filtroTag, setFiltroTag] = useState("todos");
  // Validador de telefones
  const [filtroTipoTel, setFiltroTipoTel] = useState("todos"); // todos, celular, fixo, invalido
  const [filtroWhatsApp, setFiltroWhatsApp] = useState("todos"); // todos, sim, nao
  const [validadorStats, setValidadorStats] = useState(null);
  const [validandoLista, setValidandoLista] = useState(false);
  const [removendoDup, setRemovendoDup] = useState(false);
  const [verificandoWpp, setVerificandoWpp] = useState(false);
  // Grupos / Ligações / Tag em lote
  const [numerosLigados, setNumerosLigados] = useState({});
  const [contatosTotal, setContatosTotal] = useState(0);
  const [contatosSkip, setContatosSkip] = useState(0);
  const [loadingContatos, setLoadingContatos] = useState(false);
  const [contadores, setContadores] = useState({ total: 0, celular: 0, fixo: 0, invalido: 0, nao_validado: 0, wpp_sim: 0, wpp_nao: 0, wpp_nv: 0, sms_enviado: 0, sms_nao_enviado: 0, acao_sem: 0, acao_so_sms: 0, acao_so_lig: 0, acao_ambos: 0 });
  const [contatoTagCounts, setContatoTagCounts] = useState({});
  const CONTATOS_PAGE_SIZE = 100;
  const [showNovoGrupoDlg, setShowNovoGrupoDlg] = useState(false);
  const [novoGrupoNome, setNovoGrupoNome] = useState("");
  const [editandoGrupoDlg, setEditandoGrupoDlg] = useState(null); // {antigo}
  const [grupoEditNome, setGrupoEditNome] = useState("");
  const [aplicandoTagDlg, setAplicandoTagDlg] = useState(null); // grupo (string)
  const [tagLoteSelecionada, setTagLoteSelecionada] = useState("");
  const [salvandoGrupo, setSalvandoGrupo] = useState(false);
  // Seleção de contatos + mover/excluir grupo
  const [selecionados, setSelecionados] = useState([]);
  const [showMoverGrupoDlg, setShowMoverGrupoDlg] = useState(false);
  const [grupoDestinoMover, setGrupoDestinoMover] = useState("");
  const [moverEscopo, setMoverEscopo] = useState("selecionados"); // selecionados | todos_filtrados | grupo_inteiro
  const [moverGrupoOrigem, setMoverGrupoOrigem] = useState(null); // {nome, qtd} quando "grupo_inteiro"
  const [movendoGrupo, setMovendoGrupo] = useState(false);
  const [excluindoGrupoDlg, setExcluindoGrupoDlg] = useState(null); // {nome, qtd}
  const [excluindoGrupo, setExcluindoGrupo] = useState(false);

  // Tags & Reply
  const [smsTags, setSmsTags] = useState([]);
  const [contatoTags, setContatoTags] = useState([]);
  const [showNovaTag, setShowNovaTag] = useState(false);
  const [showNovaContatoTag, setShowNovaContatoTag] = useState(false);
  const [novaTagForm, setNovaTagForm] = useState({ nome: "", cor: "#6366f1" });
  const [novaContatoTagForm, setNovaContatoTagForm] = useState({ nome: "", cor: "#6366f1" });
  const [showResponder, setShowResponder] = useState(false);
  const [respostaForm, setRespostaForm] = useState({ numero: "", texto: "", msg_id: "" });
  const [respondendo, setRespondendo] = useState(false);
  const [vistaAgrupada, setVistaAgrupada] = useState(true);
  const [numeroExpandido, setNumeroExpandido] = useState(null);
  const [chips, setChips] = useState([]);
  const [gestaoEnvio, setGestaoEnvio] = useState(null);
  const [editandoChip, setEditandoChip] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  const [horarioConfig, setHorarioConfig] = useState({ hora_inicio: "07:20", hora_fim: "19:00", ativo: true });
  const [editandoHorario, setEditandoHorario] = useState(false);
  const [horarioForm, setHorarioForm] = useState({ hora_inicio: "07:20", hora_fim: "19:00" });
  const [historicoContato, setHistoricoContato] = useState(null);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  const handleValidarLista = async () => {
    if (!window.confirm("Validar todos os contatos? Os números INVÁLIDOS serão removidos automaticamente. Esta operação pode demorar alguns minutos.")) return;
    setValidandoLista(true);
    try {
      const res = await axios.post(`${API}/sms/contatos/validar`, { auto_remover_invalidos: true, auto_remover_fixos: false });
      toast.success(`Validação concluída! Celulares: ${res.data.celular} | Fixos: ${res.data.fixo} | Inválidos removidos: ${res.data.removidos_invalidos}`);
      await carregarTudo();
    } catch (e) {
      toast.error("Erro ao validar lista: " + (e.response?.data?.detail || e.message));
    } finally {
      setValidandoLista(false);
    }
  };

  const handleRemoverDuplicatas = async () => {
    if (!window.confirm("Remover contatos duplicados? Será mantido o mais completo (com mais tags/grupo).")) return;
    setRemovendoDup(true);
    try {
      const res = await axios.post(`${API}/sms/contatos/remover-duplicatas`);
      toast.success(`${res.data.contatos_removidos} contatos duplicados removidos (${res.data.grupos_duplicados} números afetados).`);
      await carregarTudo();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setRemovendoDup(false);
    }
  };

  // Contadores agora vem do backend (server-side) via /sms/contatos/stats
  const contadoresGrupo = contadores;
  const totalContatosGeral = grupos.reduce((a, g) => a + (g.qtd || 0), 0);

  const handleCriarGrupo = async () => {
    const nome = (novoGrupoNome || "").trim();
    if (!nome) { toast.error("Digite o nome do grupo"); return; }
    setSalvandoGrupo(true);
    try {
      await axios.post(`${API}/sms/contatos/grupos/criar`, { nome });
      toast.success(`Grupo "${nome}" criado`);
      setShowNovoGrupoDlg(false);
      setNovoGrupoNome("");
      await carregarTudo();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally { setSalvandoGrupo(false); }
  };

  const handleRenomearGrupo = async () => {
    const novo = (grupoEditNome || "").trim();
    const antigo = editandoGrupoDlg?.antigo;
    if (!novo || !antigo) { toast.error("Nome inválido"); return; }
    setSalvandoGrupo(true);
    try {
      const r = await axios.put(`${API}/sms/contatos/grupos/renomear`, { antigo, novo });
      toast.success(`${r.data.atualizados} contato(s) atualizados`);
      setEditandoGrupoDlg(null);
      setGrupoEditNome("");
      if (filtroGrupo === antigo) setFiltroGrupo(novo);
      await carregarTudo();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally { setSalvandoGrupo(false); }
  };

  const handleAplicarTagLote = async () => {
    const grupo = aplicandoTagDlg;
    const tag = (tagLoteSelecionada || "").trim();
    if (!tag) { toast.error("Selecione uma tag"); return; }
    setSalvandoGrupo(true);
    try {
      const r = await axios.post(`${API}/sms/contatos/grupos/aplicar-tag`, { grupo, tag });
      toast.success(`Tag "${tag}" aplicada em ${r.data.atualizados} contato(s)`);
      setAplicandoTagDlg(null);
      setTagLoteSelecionada("");
      await carregarTudo();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally { setSalvandoGrupo(false); }
  };

  // ---- Seleção de contatos ----
  const toggleSelecionado = (id) => {
    setSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const idsPaginaAtual = contatos.map(c => c.id);
  const todosPaginaSelecionados = idsPaginaAtual.length > 0 && idsPaginaAtual.every(id => selecionados.includes(id));
  const toggleSelecionarPagina = () => {
    if (todosPaginaSelecionados) {
      setSelecionados(prev => prev.filter(id => !idsPaginaAtual.includes(id)));
    } else {
      setSelecionados(prev => Array.from(new Set([...prev, ...idsPaginaAtual])));
    }
  };
  const limparSelecao = () => setSelecionados([]);

  const abrirMoverGrupo = (escopo = "selecionados") => {
    setMoverEscopo(escopo);
    setMoverGrupoOrigem(null);
    setGrupoDestinoMover("");
    setShowMoverGrupoDlg(true);
  };

  const abrirMoverTodosGrupo = (g) => {
    setMoverEscopo("grupo_inteiro");
    setMoverGrupoOrigem({ nome: g.nome, qtd: g.qtd });
    setGrupoDestinoMover("");
    setShowMoverGrupoDlg(true);
  };

  const handleMoverGrupo = async () => {
    const destino = (grupoDestinoMover || "").trim();
    if (!destino) { toast.error("Selecione o grupo de destino"); return; }
    setMovendoGrupo(true);
    try {
      let payload = { grupo_destino: destino };
      if (moverEscopo === "grupo_inteiro") {
        payload.filtros = { grupo: moverGrupoOrigem?.nome };
      } else if (moverEscopo === "todos_filtrados") {
        payload.filtros = {
          grupo: filtroGrupo !== "todos" ? filtroGrupo : null,
          tag: filtroContatoTag !== "todos" ? filtroContatoTag : null,
          tipo_telefone: filtroTipoTel !== "todos" ? filtroTipoTel : null,
          whatsapp: filtroWhatsApp !== "todos" ? filtroWhatsApp : null,
          search: searchContato && searchContato.trim() ? searchContato.trim() : null,
        };
      } else {
        if (selecionados.length === 0) { toast.error("Nenhum contato selecionado"); setMovendoGrupo(false); return; }
        payload.ids = selecionados;
      }
      const r = await axios.post(`${API}/sms/contatos/mover-grupo`, payload);
      toast.success(`${(r.data.movidos || 0).toLocaleString('pt-BR')} contato(s) movido(s) para "${destino}"`);
      setShowMoverGrupoDlg(false);
      setMoverGrupoOrigem(null);
      setSelecionados([]);
      recarregarContatos();
      await carregarTudo();
    } catch (e) {
      toast.error("Erro ao mover: " + (e.response?.data?.detail || e.message));
    } finally { setMovendoGrupo(false); }
  };

  const handleExcluirGrupo = async () => {
    const nome = excluindoGrupoDlg?.nome;
    if (!nome) return;
    setExcluindoGrupo(true);
    try {
      const r = await axios.delete(`${API}/sms/contatos/grupos/${encodeURIComponent(nome)}`);
      toast.success(`Grupo "${nome}" excluído. ${(r.data.desvinculados || 0).toLocaleString('pt-BR')} contato(s) movido(s) para "Geral".`);
      if (filtroGrupo === nome) setFiltroGrupo("todos");
      setExcluindoGrupoDlg(null);
      recarregarContatos();
      await carregarTudo();
    } catch (e) {
      toast.error("Erro ao excluir grupo: " + (e.response?.data?.detail || e.message));
    } finally { setExcluindoGrupo(false); }
  };

  const handleVerificarWhatsApp = async (somenteVisiveis = true) => {
    const ids = somenteVisiveis ? contatosFiltrados.map(c => c.id) : null;
    if (somenteVisiveis && ids.length === 0) {
      toast.error("Nenhum contato na lista filtrada.");
      return;
    }
    if (!window.confirm(`Verificar WhatsApp de ${somenteVisiveis ? ids.length : "TODOS os celulares pendentes"} contato(s)? Pode levar tempo proporcional à quantidade.`)) return;
    setVerificandoWpp(true);
    try {
      const payload = somenteVisiveis ? { ids } : { todos_pendentes: true };
      const res = await axios.post(`${API}/sms/contatos/verificar-whatsapp`, payload);
      toast.success(`Verificados ${res.data.verificados} | Com WhatsApp: ${res.data.com_whatsapp} | Sem: ${res.data.sem_whatsapp} | Erros: ${res.data.erros}`);
      await carregarTudo();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setVerificandoWpp(false);
    }
  };

  useEffect(() => { carregarTudo(); }, []);

  // Paginacao + filtros server-side (evita baixar 65MB+48MB que travavam o sistema inteiro)
  const buildContatoParams = (skip) => {
    const p = new URLSearchParams();
    if (filtroGrupo && filtroGrupo !== "todos") p.set("grupo", filtroGrupo);
    if (filtroContatoTag && filtroContatoTag !== "todos") p.set("tag", filtroContatoTag);
    if (filtroTipoTel && filtroTipoTel !== "todos") p.set("tipo_telefone", filtroTipoTel);
    if (filtroWhatsApp && filtroWhatsApp !== "todos") p.set("whatsapp", filtroWhatsApp);
    if (filtroSmsStatus && filtroSmsStatus !== "todos") p.set("sms_status", filtroSmsStatus);
    if (filtroAcao && filtroAcao !== "todos") p.set("acao", filtroAcao);
    if (searchContato && searchContato.trim()) p.set("search", searchContato.trim());
    p.set("limit", CONTATOS_PAGE_SIZE);
    p.set("skip", skip || 0);
    return p.toString();
  };

  const fetchContatos = async (skip = 0) => {
    setLoadingContatos(true);
    try {
      const res = await axios.get(`${API}/sms/contatos?${buildContatoParams(skip)}`);
      const lista = res.data?.contatos || [];
      setContatos(lista);
      setContatosTotal(res.data?.total || 0);
      setContatosSkip(skip);
      const env = {}, lig = {};
      for (const c of lista) {
        if (c.enviado) env[c.telefone] = c.enviado;
        if (c.ligado) lig[c.telefone] = c.ligado;
      }
      setNumerosEnviados(env);
      setNumerosLigados(lig);
    } catch (e) { console.error("Erro ao carregar contatos:", e); }
    finally { setLoadingContatos(false); }
  };

  const fetchContatosStats = async () => {
    try {
      const p = new URLSearchParams();
      if (filtroGrupo && filtroGrupo !== "todos") p.set("grupo", filtroGrupo);
      if (filtroContatoTag && filtroContatoTag !== "todos") p.set("tag", filtroContatoTag);
      const res = await axios.get(`${API}/sms/contatos/stats?${p.toString()}`);
      setContadores(res.data?.counters || {});
      setContatoTagCounts(res.data?.tag_counts || {});
    } catch (e) { /* silencioso */ }
  };

  const recarregarContatos = () => { fetchContatos(0); fetchContatosStats(); };

  const carregarContatosBackground = async () => {
    try {
      const vsRes = await axios.get(`${API}/sms/contatos/validador-stats`);
      setValidadorStats(vsRes.data);
    } catch (e) {}
  };

  useEffect(() => {
    if (activeTab !== "contatos") return;
    const t = setTimeout(() => { fetchContatos(0); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filtroGrupo, filtroContatoTag, filtroTipoTel, filtroWhatsApp, filtroSmsStatus, filtroAcao, searchContato]);

  useEffect(() => {
    if (activeTab !== "contatos") return;
    fetchContatosStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filtroGrupo, filtroContatoTag]);

  const carregarTudo = async () => {
    setLoading(true);
    try {
      const [cfgRes, tplRes, campRes, grpRes, msgRes, relRes, blRes, tagsRes, chipsRes, gestaoRes, contatoTagsRes] = await Promise.all([
        axios.get(`${API}/sms/config`),
        axios.get(`${API}/sms/templates`),
        axios.get(`${API}/sms/campanhas`),
        axios.get(`${API}/sms/contatos/grupos`),
        axios.get(`${API}/sms/mensagens?limit=200`),
        axios.get(`${API}/sms/relatorio`),
        axios.get(`${API}/sms/blacklist`),
        axios.get(`${API}/sms/tags`),
        axios.get(`${API}/sms/chips`),
        axios.get(`${API}/sms/gestao-envio`),
        axios.get(`${API}/sms/contato-tags`)
      ]);
      setConfig(cfgRes.data);
      setConfigForm({ gateway_ip: cfgRes.data.gateway_ip || "", gateway_port: cfgRes.data.gateway_port || "80", default_port: cfgRes.data.default_port || 1 });
      setTemplates(tplRes.data);
      setCampanhas(campRes.data);
      setGrupos(grpRes.data);
      setMensagens(msgRes.data);
      setRelatorio(relRes.data);
      setBlacklist(blRes.data);
      setSmsTags(tagsRes.data);
      setChips(chipsRes.data);
    // Carregar saude dos chips (circuit breaker, taxa, cooldown)
    try {
      const hRes = await axios.get(`${API}/sms/chips/health`);
      const map = {};
      (hRes.data?.chips || []).forEach(h => { map[h.chip_id] = h; });
      setChipsHealth(map);
    } catch(e) { /* silencioso - endpoint pode nao existir */ }
      setGestaoEnvio(gestaoRes.data);
      setContatoTags(contatoTagsRes.data);
    } catch (e) { console.error("Erro ao carregar SMS data:", e); }
    finally { setLoading(false); }

    // Dados pesados em BACKGROUND (nao bloqueiam o render: telas abrem na hora)
    carregarContatosBackground();
    // Load horario config separately (non-blocking)
    try {
      const hRes = await axios.get(`${API}/sms/horario`);
      setHorarioConfig(hRes.data);
      setHorarioForm({ hora_inicio: hRes.data.hora_inicio || "07:20", hora_fim: hRes.data.hora_fim || "19:00" });
    } catch {}
  };

  // Auto-refresh campaigns when any is ENVIANDO
  useEffect(() => {
    const temEnviando = campanhas.some(c => c.status === "ENVIANDO");
    if (!temEnviando) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/sms/campanhas`);
        setCampanhas(res.data);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [campanhas.map(c => c.status).join(",")]);

  // Refresh leve: so campanhas, templates e relatorio
  const recarregarCampanhasApenas = async () => {
    try {
      const [cRes, tRes, rRes] = await Promise.all([
        axios.get(`${API}/sms/campanhas`),
        axios.get(`${API}/sms/templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/sms/relatorio`).catch(() => ({ data: {} })),
      ]);
      setCampanhas(cRes.data);
      if (Array.isArray(tRes.data)) setTemplates(tRes.data);
      if (rRes.data) setRelatorio(rRes.data);
    } catch(e) { /* ignore */ }
  };

  const salvarConfig = async () => {
    try {
      await axios.put(`${API}/sms/config`, configForm);
      toast.success("Configuração salva!");
      setShowConfig(false);
      carregarTudo();
    } catch (e) { toast.error("Erro ao salvar configuração"); }
  };

  const salvarTemplate = async () => {
    try {
      if (!templateForm.nome || !templateForm.texto) { toast.error("Nome e texto são obrigatórios"); return; }
      await axios.post(`${API}/sms/templates`, templateForm);
      toast.success("Template criado!");
      setShowNovoTemplate(false);
      setTemplateForm({ nome: "", categoria: "FGTS", texto: "" });
      recarregarCampanhasApenas();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar template"); }
  };

  const excluirTemplate = async (id) => {
    try {
      await axios.delete(`${API}/sms/templates/${id}`);
      toast.success("Template excluído!");
      carregarTudo();
    } catch (e) { toast.error("Erro ao excluir"); }
  };

  const salvarCampanha = async () => {
    try {
      if (!campanhaForm.nome) { toast.error("Nome é obrigatório"); return; }
      if (!campanhaForm.texto && !campanhaForm.template_id) { toast.error("Texto ou template é obrigatório"); return; }
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      // If template selected, get its text
      let textoFinal = campanhaForm.texto;
      let templateNome = "";
      if (campanhaForm.template_id) {
        const tpl = templates.find(t => t.id === campanhaForm.template_id);
        if (tpl) { textoFinal = textoFinal || tpl.texto; templateNome = tpl.nome; }
      }

      if (campanhaOrigem === "arquivo") {
        // File upload mode
        if (!campanhaArquivo) { toast.error("Selecione um arquivo CSV ou Excel"); return; }
        setUploadingCampanha(true);
        const formData = new FormData();
        formData.append("file", campanhaArquivo);
        formData.append("nome", campanhaForm.nome);
        formData.append("texto", textoFinal);
        formData.append("template_id", campanhaForm.template_id || "");
        formData.append("template_nome", templateNome);
        formData.append("created_by", usuario.nome || "admin");
        await axios.post(`${API}/sms/campanhas/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } else {
        // Group mode (existing behavior)
        await axios.post(`${API}/sms/campanhas`, { ...campanhaForm, texto: textoFinal, template_nome: templateNome, created_by: usuario.nome || "admin" });
      }

      toast.success("Campanha criada!");
      setShowNovaCampanha(false);
      setCampanhaForm({ nome: "", template_id: "", texto: "", grupo_contato: "", tipo_telefone: "todos", whatsapp_status: "todos", sms_status: "todos", acao: "todos" });
      setCampanhaOrigem("grupo");
      setCampanhaArquivo(null);
      setUploadingCampanha(false);
      carregarTudo();
    } catch (e) {
      setUploadingCampanha(false);
      toast.error(e.response?.data?.detail || "Erro ao criar campanha");
    }
  };

  const [gerandoClt, setGerandoClt] = useState(false);
  const gerarCampanhaCltComOferta = async () => {
    try {
      setGerandoClt(true);
      const prev = await axios.get(`${API}/sms/campanhas/prospecta-clt-com-oferta/preview`);
      const eleg = prev.data?.elegiveis || 0;
      if (!eleg) { toast.error("Nenhum lead com oferta real e telefone válido no momento."); return; }
      if (!window.confirm(`Criar campanha SMS personalizada para ${eleg} lead(s) Prospecta CLT COM oferta real?\n\nCada contato recebe banco/valor/prazo/parcela reais. Leads SEM oferta não entram.`)) return;
      const res = await axios.post(`${API}/sms/campanhas/prospecta-clt-com-oferta`, { created_by: usuario.nome || "admin" });
      toast.success(`Campanha "${res.data.nome}" criada com ${res.data.total_contatos} contatos! Revise e clique em Enviar.`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar campanha CLT"); }
    finally { setGerandoClt(false); }
  };


  const enviarCampanha = async (campanhaId) => {
    try {
      setEnviando(true);
      const res = await axios.post(`${API}/sms/campanhas/${campanhaId}/enviar`);
      toast.success(res.data.message || `Envio iniciado para ${res.data.total} contatos!`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar campanha"); }
    finally { setEnviando(false); }
  };

  const pausarCampanha = async (campanhaId) => {
    try {
      await axios.post(`${API}/sms/campanhas/${campanhaId}/pausar`);
      toast.success("Campanha pausada!");
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao pausar"); }
  };

  const retomarCampanha = async (campanhaId) => {
    try {
      const res = await axios.post(`${API}/sms/campanhas/${campanhaId}/retomar`);
      toast.success(res.data.message || "Campanha retomada!");
      carregarTudo();
      // If report is open, refresh it and start auto-refresh
      if (relatorioCampanha && relatorioCampanha.campanha?.id === campanhaId) {
        abrirRelatorio(campanhaId);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao retomar"); }
  };

  const salvarHorario = async () => {
    try {
      const res = await axios.put(`${API}/sms/horario`, horarioForm);
      setHorarioConfig({ ...horarioConfig, ...horarioForm });
      setEditandoHorario(false);
      toast.success("Horario atualizado!");
    } catch (e) { toast.error("Erro ao salvar horario"); }
  };

  const finalizarCampanha = async (campanhaId) => {
    if (!window.confirm("Finalizar campanha? Isso vai marcar como concluída e remover os contatos pendentes.")) return;
    try {
      const res = await axios.post(`${API}/sms/campanhas/${campanhaId}/finalizar`);
      toast.success(`Campanha finalizada! ${res.data.pendentes_removidos} pendentes removidos.`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao finalizar"); }
  };

  const exportarRelatorioExcel = async (campanhaId) => {
    try {
      const res = await axios.get(`${API}/sms/campanhas/${campanhaId}/relatorio/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1] : `relatorio_${campanhaId}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório exportado!");
    } catch (e) { toast.error("Erro ao exportar relatório"); }
  };

  const reenviarCampanha = async (campanhaId, modo) => {
    const label = modo === "todos" ? "TODOS os contatos (do início)" : "apenas os que FALHARAM";
    if (!window.confirm(`Reenviar campanha para ${label}?`)) return;
    try {
      const res = await axios.post(`${API}/sms/campanhas/${campanhaId}/reenviar`, { modo });
      toast.success(res.data.message || "Reenvio iniciado!");
      setShowRelatorio(false);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reenviar"); }
  };

  const [showEditarCampanha, setShowEditarCampanha] = useState(false);
  const [editCampanhaForm, setEditCampanhaForm] = useState({ id: "", nome: "", texto: "", template_id: "" });

  const abrirEditarCampanha = (campanha) => {
    setEditCampanhaForm({ id: campanha.id, nome: campanha.nome, texto: campanha.texto || "", template_id: campanha.template_id || "" });
    setShowEditarCampanha(true);
  };

  const salvarEdicaoCampanha = async () => {
    try {
      if (!editCampanhaForm.nome.trim()) { toast.error("Nome é obrigatório"); return; }
      await axios.put(`${API}/sms/campanhas/${editCampanhaForm.id}`, {
        nome: editCampanhaForm.nome,
        texto: editCampanhaForm.texto,
        template_id: editCampanhaForm.template_id
      });
      toast.success("Campanha atualizada!");
      setShowEditarCampanha(false);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao editar"); }
  };

  const [showRelatorio, setShowRelatorio] = useState(false);
  const [relatorioCampanha, setRelatorioCampanha] = useState(null);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [relatorioAutoRefresh, setRelatorioAutoRefresh] = useState(null);

  const abrirRelatorio = async (campanhaId) => {
    setLoadingRelatorio(true);
    setShowRelatorio(true);
    try {
      const res = await axios.get(`${API}/sms/campanhas/${campanhaId}/relatorio`);
      setRelatorioCampanha(res.data);
      // Auto-refresh if campaign is still sending
      if (res.data.campanha?.status === "ENVIANDO") {
        const interval = setInterval(async () => {
          try {
            const r = await axios.get(`${API}/sms/campanhas/${campanhaId}/relatorio`);
            setRelatorioCampanha(r.data);
            if (r.data.campanha?.status !== "ENVIANDO") {
              clearInterval(interval);
              setRelatorioAutoRefresh(null);
              carregarTudo();
            }
          } catch { /* ignore */ }
        }, 3000);
        setRelatorioAutoRefresh(interval);
      }
    } catch { toast.error("Erro ao carregar relatório"); setShowRelatorio(false); }
    finally { setLoadingRelatorio(false); }
  };

  useEffect(() => {
    return () => { if (relatorioAutoRefresh) clearInterval(relatorioAutoRefresh); };
  }, [relatorioAutoRefresh]);

  const excluirCampanha = async (id) => {
    try {
      await axios.delete(`${API}/sms/campanhas/${id}`);
      toast.success("Campanha excluída!");
      carregarTudo();
    } catch (e) { toast.error("Erro ao excluir"); }
  };

  const salvarContato = async () => {
    try {
      if (!contatoForm.telefone) { toast.error("Telefone é obrigatório"); return; }
      await axios.post(`${API}/sms/contatos`, contatoForm);
      toast.success("Contato adicionado!");
      setShowNovoContato(false);
      setContatoForm({ nome: "", telefone: "", grupo: "Geral", tags: [] });
      carregarTudo();
      recarregarContatos();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao adicionar contato"); }
  };

  const previewImportarArquivo = async () => {
    if (!importForm.file) { toast.error("Selecione um arquivo"); return; }
    setLoadingPreview(true);
    setPreviewData(null);
    try {
      const formData = new FormData();
      formData.append("file", importForm.file);
      const res = await axios.post(`${API}/sms/contatos/importar-arquivo/preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setPreviewData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao analisar arquivo");
    } finally {
      setLoadingPreview(false);
    }
  };

  const importarContatos = async () => {
    if (importingContatos) { return; }
    setImportingContatos(true);
    try {
      if (importForm.origem === "arquivo") {
        if (!importForm.file) { toast.error("Selecione um arquivo"); return; }
        const formData = new FormData();
        formData.append("file", importForm.file);
        formData.append("grupo", importForm.grupo || "Importado");
        formData.append("tags", JSON.stringify(importForm.tags || []));
        const res = await axios.post(`${API}/sms/contatos/importar-arquivo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(`${res.data.importados} contatos importados! ${res.data.duplicados || 0} duplicado(s) ignorado(s).`);
      } else {
        const res = await axios.post(`${API}/sms/contatos/importar`, { ...importForm, tags: importForm.tags || [] });
        toast.success(`${res.data.importados} contatos importados! ${res.data.duplicados || 0} duplicado(s) ignorado(s).`);
      }
      setShowImportarContatos(false);
      setImportForm({ origem: "clientes", grupo: "Clientes", tag: "", tags: [] });
      setPreviewData(null);
      carregarTudo();
      recarregarContatos();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao importar"); }
    finally { setImportingContatos(false); }
  };

  const excluirContato = async (id) => {
    try {
      await axios.delete(`${API}/sms/contatos/${id}`);
      toast.success("Contato removido!");
      carregarTudo();
      recarregarContatos();
    } catch (e) { toast.error("Erro ao excluir"); }
  };

  const abrirHistorico = async (contatoId) => {
    setLoadingHistorico(true);
    setShowHistorico(true);
    try {
      const res = await axios.get(`${API}/sms/contatos/${contatoId}/historico`);
      setHistoricoContato(res.data);
    } catch { toast.error("Erro ao carregar histórico"); setShowHistorico(false); }
    finally { setLoadingHistorico(false); }
  };

  const toggleContatoTag = async (contatoId, tagNome) => {
    const contato = contatos.find(c => c.id === contatoId);
    if (!contato) return;
    const currentTags = contato.tags || [];
    const newTags = currentTags.includes(tagNome)
      ? currentTags.filter(t => t !== tagNome)
      : [...currentTags, tagNome];
    try {
      await axios.put(`${API}/sms/contatos/${contatoId}/tags`, { tags: newTags });
      setContatos(prev => prev.map(c => c.id === contatoId ? { ...c, tags: newTags } : c));
      toast.success(newTags.includes(tagNome) ? `Tag "${tagNome}" adicionada` : `Tag "${tagNome}" removida`);
    } catch { toast.error("Erro ao atualizar tags"); }
  };

  const enviarSms = async () => {
    try {
      if (!smsTexto && !smsTemplateId) { toast.error("Texto ou template é obrigatório"); return; }
      if (!smsNumeros.trim()) { toast.error("Informe ao menos um número"); return; }
      setEnviando(true);
      const numeros = smsNumeros.split(/[,;\n]+/).map(n => n.trim()).filter(Boolean);
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const res = await axios.post(`${API}/sms/enviar`, {
        numeros, texto: smsTexto, template_id: smsTemplateId || undefined, enviado_por: usuario.nome || "admin"
      });
      if (res.data.success) {
        toast.success(`${res.data.enviados} SMS enviado(s)!${res.data.bloqueados ? ` (${res.data.bloqueados} bloqueado(s))` : ""}${res.data.duplicados ? ` (${res.data.duplicados} duplicado(s) ignorado(s))` : ""}`);
        setSmsNumeros("");
        setSmsTexto("");
        setSmsTemplateId("");
        carregarTudo();
      } else {
        toast.error(res.data.erro || "Erro ao enviar SMS", { duration: 8000 });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar SMS"); }
    finally { setEnviando(false); }
  };

  const receberSms = async () => {
    try {
      const res = await axios.post(`${API}/sms/receber`);
      if (res.data.success) {
        toast.success(`${res.data.novas} nova(s) mensagem(ns) recebida(s)`);
        carregarTudo();
      } else {
        toast.error(res.data.error || "Erro ao buscar SMS");
      }
    } catch (e) { toast.error("Erro ao buscar SMS recebidos"); }
  };

  const adicionarBlacklist = async () => {
    try {
      if (!blacklistForm.telefone) { toast.error("Telefone é obrigatório"); return; }
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      await axios.post(`${API}/sms/blacklist`, { ...blacklistForm, adicionado_por: usuario.nome || "admin" });
      toast.success("Número adicionado à blacklist!");
      setShowNovoBlacklist(false);
      setBlacklistForm({ telefone: "", motivo: "" });
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao adicionar"); }
  };

  const excluirBlacklist = async (id) => {
    try {
      await axios.delete(`${API}/sms/blacklist/${id}`);
      toast.success("Número removido da blacklist!");
      carregarTudo();
    } catch (e) { toast.error("Erro ao remover"); }
  };

  const importarBlacklistArquivo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingBlacklist(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/sms/blacklist/importar`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.importados} número(s) importado(s)! ${res.data.duplicados} duplicado(s) ignorado(s).`);
      carregarTudo();
    } catch (err) { toast.error("Erro ao importar arquivo"); }
    finally { setUploadingBlacklist(false); e.target.value = ""; }
  };

  const blacklistFiltrada = blacklist.filter(b =>
    !searchBlacklist || b.telefone?.includes(searchBlacklist) || b.motivo?.toLowerCase().includes(searchBlacklist.toLowerCase())
  );

  const toggleVerificado = async (msgId) => {
    try {
      const res = await axios.put(`${API}/sms/mensagens/${msgId}/verificar`);
      setMensagens(prev => prev.map(m => m.id === msgId ? { ...m, verificado: res.data.verificado } : m));
      toast.success(res.data.verificado ? "Marcado como verificado" : "Verificação removida");
    } catch { toast.error("Erro ao atualizar"); }
  };

  const setTagMensagem = async (msgId, tag) => {
    try {
      await axios.put(`${API}/sms/mensagens/${msgId}/tag`, { tag });
      setMensagens(prev => prev.map(m => m.id === msgId ? { ...m, tag } : m));
      toast.success(tag ? `Tag "${tag}" aplicada` : "Tag removida");
    } catch { toast.error("Erro ao atualizar tag"); }
  };

  const adicionarBlacklistDireto = async (numero) => {
    try {
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      await axios.post(`${API}/sms/blacklist`, { telefone: numero, motivo: "Bloqueado via caixa de entrada", adicionado_por: usuario.nome || "admin" });
      toast.success(`${numero} adicionado à blacklist!`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao bloquear número"); }
  };

  const responderSms = async () => {
    if (!respostaForm.texto.trim()) { toast.error("Digite a mensagem"); return; }
    setRespondendo(true);
    try {
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const res = await axios.post(`${API}/sms/responder`, { ...respostaForm, enviado_por: usuario.nome || "admin" });
      if (res.data.success) {
        toast.success("Resposta enviada! Mensagem marcada como Respondida");
        setShowResponder(false);
        // Update local state: mark original as Respondida + Verificado
        setMensagens(prev => prev.map(m => m.id === respostaForm.msg_id ? { ...m, tag: "Respondida", verificado: true } : m));
        setRespostaForm({ numero: "", texto: "", msg_id: "" });
        carregarTudo();
      } else { toast.error(res.data.error || "Erro ao responder"); }
    } catch (e) { toast.error("Erro ao enviar resposta"); }
    finally { setRespondendo(false); }
  };

  const criarTag = async () => {
    if (!novaTagForm.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      await axios.post(`${API}/sms/tags`, novaTagForm);
      toast.success("Tag criada!");
      setShowNovaTag(false);
      setNovaTagForm({ nome: "", cor: "#6366f1" });
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar tag"); }
  };

  const excluirTag = async (tagId) => {
    try {
      await axios.delete(`${API}/sms/tags/${tagId}`);
      toast.success("Tag excluída!");
      carregarTudo();
    } catch { toast.error("Erro ao excluir tag"); }
  };

  const criarContatoTag = async () => {
    if (!novaContatoTagForm.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      await axios.post(`${API}/sms/contato-tags`, novaContatoTagForm);
      toast.success("Tag de contato criada!");
      setNovaContatoTagForm({ nome: "", cor: "#6366f1" });
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar tag"); }
  };

  const excluirContatoTag = async (tagId) => {
    try {
      await axios.delete(`${API}/sms/contato-tags/${tagId}`);
      toast.success("Tag de contato excluída!");
      carregarTudo();
    } catch { toast.error("Erro ao excluir tag"); }
  };

  // Refresh leve: so chips e health (evita baixar 81MB de contatos)
  const recarregarChipsApenas = async () => {
    try {
      const [cRes, hRes] = await Promise.all([
        axios.get(`${API}/sms/chips`),
        axios.get(`${API}/sms/chips/health`).catch(() => ({ data: { chips: [] } })),
      ]);
      setChips(cRes.data);
      const map = {};
      (hRes.data?.chips || []).forEach(h => { map[h.chip_id] = h; });
      setChipsHealth(map);
    } catch(e) { /* ignore */ }
  };

  const atualizarChip = async (chipId, data) => {
    try {
      await axios.put(`${API}/sms/chips/${chipId}`, data);
      toast.success("Chip atualizado!");
      setEditandoChip(null);
      recarregarChipsApenas();
    } catch { toast.error("Erro ao atualizar chip"); }
  };

  const categoriasTemplate = ["FGTS", "INSS", "Portabilidade", "Cartão", "Promoção", "Cobrança", "Geral"];

  const contatosFiltrados = contatos; // filtragem agora e server-side (paginada)

  const mensagensFiltradas = mensagens
    .filter(m => filtroMsgTipo === "todos" || m.tipo === filtroMsgTipo)
    .filter(m => filtroVerificado === "todos" || (filtroVerificado === "verificado" ? m.verificado : !m.verificado))
    .filter(m => filtroTag === "todos" || m.tag === filtroTag);

  // Count messages per tag (only RECEBIDO type)
  const recebidas = mensagens.filter(m => m.tipo === "RECEBIDO");
  const tagCounts = smsTags.reduce((acc, t) => {
    acc[t.nome] = recebidas.filter(m => m.tag === t.nome).length;
    return acc;
  }, {});
  const verificadasCount = recebidas.filter(m => m.verificado).length;
  const pendentesCount = recebidas.filter(m => !m.verificado).length;

  // Group messages by phone number for consolidated view
  const mensagensAgrupadas = useMemo(() => {
    const grupos = {};
    mensagensFiltradas.forEach(m => {
      if (!grupos[m.numero]) {
        grupos[m.numero] = { numero: m.numero, msgs: [], totalRecebidas: 0, totalEnviadas: 0, respondida: false, verificada: false, ultimaTag: "", ultimaData: "" };
      }
      grupos[m.numero].msgs.push(m);
      if (m.tipo === "RECEBIDO") grupos[m.numero].totalRecebidas++;
      if (m.tipo === "ENVIADO") grupos[m.numero].totalEnviadas++;
      if (m.tag === "Respondida" || m.resposta_para) grupos[m.numero].respondida = true;
      if (m.verificado) grupos[m.numero].verificada = true;
      if (m.tag && m.tag !== "Respondida") grupos[m.numero].ultimaTag = m.tag;
      if (!grupos[m.numero].ultimaData || m.created_at > grupos[m.numero].ultimaData) {
        grupos[m.numero].ultimaData = m.created_at;
        if (!grupos[m.numero].ultimaMsg || m.tipo === "RECEBIDO") grupos[m.numero].ultimaMsg = m;
      }
    });
    return Object.values(grupos).sort((a, b) => (b.ultimaData || "").localeCompare(a.ultimaData || ""));
  }, [mensagensFiltradas]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="sms-marketing-title">{pageTitle}</h1>
          <p className="text-gray-500 mt-1">{pageSubtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.configurado ? "default" : "destructive"} className={`gap-1 ${config.configurado ? "bg-green-600" : ""}`}>
            {config.configurado ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
            {config.configurado ? "Gateway Conectado" : "Gateway Não Configurado"}
          </Badge>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowConfig(true)} data-testid="btn-config-gateway">
            <Settings className="h-4 w-4" /> Configurar Gateway
          </Button>
        </div>
      </div>

      {/* Stats */}
      {!_isSingleTab && <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">SMS Enviados</p>
                <p className="text-2xl font-bold text-blue-700">{relatorio.total_enviados}</p>
              </div>
              <Send className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">SMS Recebidos</p>
                <p className="text-2xl font-bold text-green-700">{relatorio.total_recebidos}</p>
              </div>
              <Inbox className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">Campanhas</p>
                <p className="text-2xl font-bold text-purple-700">{relatorio.campanhas_enviadas}/{relatorio.campanhas_total}</p>
              </div>
              <Megaphone className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">Contatos</p>
                <p className="text-2xl font-bold text-orange-700">{relatorio.total_contatos}</p>
              </div>
              <Users className="h-8 w-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        {!_isSingleTab && <div className="overflow-x-auto">
          <TabsList className="inline-flex gap-1 bg-transparent p-1 min-w-max">
            {_tabAllowed("enviar") && (<TabsTrigger value="enviar" className="gap-2 px-3 py-2 data-[state=active]:bg-blue-100">
              <Send className="h-4 w-4 text-blue-600" />
              <span className="text-blue-600 font-medium">Enviar SMS</span>
            </TabsTrigger>)}
            {_tabAllowed("templates") && (<TabsTrigger value="templates" className="gap-2 px-3 py-2 data-[state=active]:bg-green-100">
              <FileText className="h-4 w-4 text-green-600" />
              <span className="text-green-600 font-medium">Templates</span>
            </TabsTrigger>)}
            {_tabAllowed("campanhas") && (<TabsTrigger value="campanhas" className="gap-2 px-3 py-2 data-[state=active]:bg-purple-100">
              <Megaphone className="h-4 w-4 text-purple-600" />
              <span className="text-purple-600 font-medium">Campanhas</span>
            </TabsTrigger>)}
{_tabAllowed("caixa") && (<TabsTrigger value="caixa" className="gap-2 px-3 py-2 data-[state=active]:bg-teal-100">
              <Inbox className="h-4 w-4 text-teal-600" />
              <span className="text-teal-600 font-medium">Caixa de Entrada</span>
            </TabsTrigger>)}
            {_tabAllowed("gestao") && (<TabsTrigger value="gestao" className="gap-2 px-3 py-2 data-[state=active]:bg-amber-100">
              <Settings className="h-4 w-4 text-amber-600" />
              <span className="text-amber-600 font-medium">Gestão de Chips</span>
            </TabsTrigger>)}
            {_tabAllowed("relatorios") && (<TabsTrigger value="relatorios" className="gap-2 px-3 py-2 data-[state=active]:bg-slate-100">
              <BarChart3 className="h-4 w-4 text-slate-600" />
              <span className="text-slate-600 font-medium">Relatórios</span>
            </TabsTrigger>)}
            {_tabAllowed("blacklist") && (<TabsTrigger value="blacklist" className="gap-2 px-3 py-2 data-[state=active]:bg-red-100">
              <ShieldBan className="h-4 w-4 text-red-600" />
              <span className="text-red-600 font-medium">Blacklist</span>
              {blacklist.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs h-5 px-1.5">{blacklist.length}</Badge>}
            </TabsTrigger>)}
          </TabsList>
        </div>}

        {/* === Tab: Enviar SMS === */}
        <TabsContent value="enviar" className="space-y-4" data-testid="tab-enviar-sms">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4 text-blue-600" /> Enviar SMS</CardTitle>
                <CardDescription>Envie SMS individual ou para múltiplos números</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Template (opcional)</Label>
                  <Select value={smsTemplateId} onValueChange={(v) => {
                    setSmsTemplateId(v);
                    const tpl = templates.find(t => t.id === v);
                    if (tpl) setSmsTexto(tpl.texto);
                  }}>
                    <SelectTrigger data-testid="select-sms-template"><SelectValue placeholder="Selecionar template" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value=" ">Nenhum (texto livre)</SelectItem>
                      {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.nome} ({t.categoria})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Números (um por linha ou separados por vírgula) *</Label>
                  <Textarea value={smsNumeros} onChange={(e) => setSmsNumeros(e.target.value)} placeholder={"71999999999\n11988888888\n21977777777"} rows={4} data-testid="input-sms-numeros" />
                </div>
                <div>
                  <Label>Texto da Mensagem *</Label>
                  <Textarea value={smsTexto} onChange={(e) => setSmsTexto(e.target.value)} placeholder="Digite o texto do SMS..." rows={4} data-testid="input-sms-texto" />
                  <p className="text-xs text-gray-400 mt-1">{smsTexto.length}/160 caracteres</p>
                </div>
                <Button className="w-full gap-2 bg-blue-600 hover:bg-blue-700" onClick={enviarSms} disabled={enviando} data-testid="btn-enviar-sms">
                  {enviando ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {enviando ? "Enviando..." : "Enviar SMS"}
                </Button>
              </CardContent>
            </Card>
            {/* SMS recentes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Envios Recentes</CardTitle>
              </CardHeader>
              <CardContent>
                {mensagens.filter(m => m.tipo === "ENVIADO").length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Send className="h-10 w-10 mx-auto mb-2 opacity-20" />
                    <p>Nenhum SMS enviado ainda</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[350px] overflow-y-auto">
                    {mensagens.filter(m => m.tipo === "ENVIADO").slice(0, 15).map(m => (
                      <div key={m.id} className="p-2 rounded border bg-white text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-xs text-blue-600">{m.numero}</span>
                          <Badge className={m.status === "ENVIADO" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} variant="outline">
                            {m.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 truncate">{m.texto}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(m.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === Tab: Templates === */}
        <TabsContent value="templates" className="space-y-4" data-testid="tab-templates">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-green-600" /> Templates de SMS</CardTitle>
                <CardDescription>Modelos prontos para campanhas e envios</CardDescription>
              </div>
              <Button className="gap-1 bg-green-600 hover:bg-green-700" onClick={() => setShowNovoTemplate(true)} data-testid="btn-novo-template">
                <Plus className="h-4 w-4" /> Novo Template
              </Button>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-lg font-medium">Nenhum template cadastrado</p>
                  <p className="text-sm">Crie templates para usar em campanhas e envios rápidos</p>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {templates.map(tpl => (
                    <Card key={tpl.id} className="bg-white" data-testid={`template-card-${tpl.id}`}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold text-sm">{tpl.nome}</h4>
                            <Badge variant="outline" className="text-xs mt-1">{tpl.categoria}</Badge>
                          </div>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluirTemplate(tpl.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded mt-2 break-words whitespace-pre-wrap">{tpl.texto}</p>
                        <p className="text-xs text-gray-400 mt-2">{tpl.texto?.length || 0} caracteres</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Tab: Campanhas === */}
        <TabsContent value="campanhas" className="space-y-4" data-testid="tab-campanhas">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-purple-600" /> Campanhas</CardTitle>
                <CardDescription>Gerencie campanhas de envio em massa</CardDescription>
              </div>
              <div className="flex items-center gap-3">
                {!editandoHorario ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setEditandoHorario(true)} data-testid="horario-display">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">Envio: {horarioConfig.hora_inicio} - {horarioConfig.hora_fim}</span>
                    <Edit className="h-3 w-3 text-blue-400" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-300 rounded-lg shadow-sm" data-testid="horario-edit">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <input type="time" value={horarioForm.hora_inicio} onChange={e => setHorarioForm({...horarioForm, hora_inicio: e.target.value})} className="text-xs border rounded px-1.5 py-0.5 w-20" data-testid="horario-inicio-input" />
                    <span className="text-xs text-gray-500">ate</span>
                    <input type="time" value={horarioForm.hora_fim} onChange={e => setHorarioForm({...horarioForm, hora_fim: e.target.value})} className="text-xs border rounded px-1.5 py-0.5 w-20" data-testid="horario-fim-input" />
                    <Button size="sm" className="h-6 text-[10px] px-2 bg-blue-600 hover:bg-blue-700" onClick={salvarHorario} data-testid="horario-salvar"><Check className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1" onClick={() => setEditandoHorario(false)}><X className="h-3 w-3" /></Button>
                  </div>
                )}
                <Button className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={gerarCampanhaCltComOferta} disabled={gerandoClt} data-testid="btn-gerar-campanha-clt-oferta">
                  <Megaphone className="h-4 w-4" /> {gerandoClt ? "Gerando..." : "Gerar CLT (Com Oferta)"}
                </Button>
                <Button className="gap-1 bg-purple-600 hover:bg-purple-700" onClick={() => setShowNovaCampanha(true)} data-testid="btn-nova-campanha">
                  <Plus className="h-4 w-4" /> Nova Campanha
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {campanhas.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-lg font-medium">Nenhuma campanha</p>
                  <p className="text-sm">Crie campanhas para enviar SMS em massa</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead className="text-center">Contatos</TableHead>
                      <TableHead className="text-center">Enviados</TableHead>
                      <TableHead className="text-center">Erros</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tempo</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campanhas.map(c => (
                      <TableRow key={c.id} data-testid={`campanha-row-${c.id}`}>
                        <TableCell className="font-medium">{c.nome}</TableCell>
                        <TableCell className="text-sm text-gray-600">{c.template_nome || "-"}</TableCell>
                        <TableCell><Badge variant="outline">{c.origem === "arquivo" ? "Arquivo" : c.grupo_contato === "__nao_enviados__" ? "Não Enviados" : c.grupo_contato || "Todos"}</Badge></TableCell>
                        <TableCell className="text-center">{c.total_contatos}</TableCell>
                        <TableCell className="text-center font-medium text-green-600">{c.enviados}</TableCell>
                        <TableCell className="text-center font-medium text-red-600">{c.erros}</TableCell>
                        <TableCell>
                          <Badge className={
                            c.status === "ENVIADA" ? "bg-green-100 text-green-700" :
                            c.status === "CONCLUIDA" ? "bg-green-100 text-green-700" :
                            c.status === "ENVIANDO" ? "bg-blue-100 text-blue-700 animate-pulse" :
                            c.status === "PAUSADA" ? "bg-orange-100 text-orange-700" :
                            c.status === "ERRO_PARCIAL" ? "bg-orange-100 text-orange-700" :
                            c.status === "ERRO" ? "bg-red-100 text-red-700" :
                            c.status === "RASCUNHO" ? "bg-yellow-100 text-yellow-700" :
                            "bg-gray-100 text-gray-700"
                          }>{c.status === "ENVIANDO" ? "ENVIANDO..." : c.status === "CONCLUIDA" ? "CONCLUÍDA" : c.status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {(() => {
                            const processados = (c.enviados || 0) + (c.erros || 0);
                            const total = c.total_contatos || 0;
                            if (c.status === "ENVIADA" && c.inicio_envio && c.data_envio) {
                              const duracao = (new Date(c.data_envio) - new Date(c.inicio_envio)) / 1000;
                              if (duracao < 60) return <span className="text-green-600 font-medium">{Math.round(duracao)}s</span>;
                              if (duracao < 3600) return <span className="text-green-600 font-medium">{Math.round(duracao / 60)}min</span>;
                              return <span className="text-green-600 font-medium">{Math.round(duracao / 3600)}h {Math.round((duracao % 3600) / 60)}min</span>;
                            }
                            if (c.status === "CONCLUIDA") {
                              return <span className="text-green-600 font-medium text-xs">100%</span>;
                            }
                            if (c.status === "ENVIANDO" && c.inicio_envio && processados > 0) {
                              const elapsed = (Date.now() - new Date(c.inicio_envio).getTime()) / 1000;
                              const rate = processados / elapsed;
                              const restante = total - processados;
                              const eta = restante / rate;
                              const pct = Math.round(processados / total * 100);
                              let etaText = "";
                              if (eta < 60) etaText = `~${Math.round(eta)}s`;
                              else if (eta < 3600) etaText = `~${Math.round(eta / 60)}min`;
                              else etaText = `~${Math.round(eta / 3600)}h ${Math.round((eta % 3600) / 60)}m`;
                              return (
                                <div className="space-y-1">
                                  <span className="text-blue-600 font-medium text-xs">{etaText} restante</span>
                                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                  </div>
                                  <span className="text-[10px] text-gray-400">{pct}% · {(rate * 60).toFixed(0)} sms/min</span>
                                </div>
                              );
                            }
                            if (c.status === "PAUSADA" && processados > 0) {
                              const pct = Math.round(processados / total * 100);
                              return <span className="text-orange-500 text-xs">{pct}% pausado</span>;
                            }
                            return <span className="text-gray-400 text-xs">-</span>;
                          })()}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(c.data_envio || c.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            {c.status === "ENVIANDO" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => pausarCampanha(c.id)} data-testid={`btn-pausar-campanha-${c.id}`}>
                                <Pause className="h-3 w-3" /> Pausar
                              </Button>
                            )}
                            {(c.status === "PAUSADA" || c.status === "ERRO_PARCIAL" || c.status === "ERRO") && (
                              <Button size="sm" className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={() => retomarCampanha(c.id)} data-testid={`btn-retomar-campanha-${c.id}`}>
                                <Play className="h-3 w-3" /> Retomar
                              </Button>
                            )}
                            {(c.status === "PAUSADA" || c.status === "ERRO_PARCIAL" || c.status === "ERRO") && (
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-green-300 text-green-600 hover:bg-green-50" onClick={() => finalizarCampanha(c.id)} data-testid={`btn-finalizar-campanha-${c.id}`}>
                                <CheckCircle className="h-3 w-3" /> Finalizar
                              </Button>
                            )}
                            {c.status === "RASCUNHO" && (
                              <Button size="sm" className="h-7 text-xs gap-1 bg-purple-600 hover:bg-purple-700" onClick={() => enviarCampanha(c.id)} disabled={enviando} data-testid={`btn-enviar-campanha-${c.id}`}>
                                <Rocket className="h-3 w-3" /> Enviar
                              </Button>
                            )}
                            {(c.status === "ENVIADA" || c.status === "CONCLUIDA" || c.status === "ERRO") && (
                              <Button size="sm" className="h-7 text-xs gap-1 bg-purple-600 hover:bg-purple-700" onClick={() => enviarCampanha(c.id)} disabled={enviando} data-testid={`btn-reenviar-campanha-${c.id}`}>
                                <Rocket className="h-3 w-3" /> Reenviar
                              </Button>
                            )}
                            {c.status !== "ENVIANDO" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => abrirEditarCampanha(c)} data-testid={`btn-editar-campanha-${c.id}`}>
                                <Edit className="h-3 w-3" /> Editar
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => abrirRelatorio(c.id)} data-testid={`btn-relatorio-campanha-${c.id}`}>
                              <BarChart3 className="h-3 w-3" /> Relatório
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluirCampanha(c.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Tab: Contatos === */}
        <TabsContent value="contatos" className="space-y-4" data-testid="tab-contatos">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-orange-600" /> Contatos</CardTitle>
                <CardDescription>{(totalContatosGeral || contatosTotal).toLocaleString('pt-BR')} contato(s) cadastrado(s)</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowNovoGrupoDlg(true)} data-testid="btn-novo-grupo">
                  <Plus className="h-4 w-4 text-emerald-600" /> Novo Grupo
                </Button>
                <Button variant="outline" size="sm" className="gap-1" onClick={handleValidarLista} disabled={validandoLista} data-testid="btn-validar-lista">
                  <CheckCircle className="h-4 w-4 text-blue-600" /> {validandoLista ? "Validando..." : "Validar Lista"}
                </Button>
                <Button variant="outline" size="sm" className="gap-1" onClick={handleRemoverDuplicatas} disabled={removendoDup} data-testid="btn-remover-duplicatas">
                  <ShieldBan className="h-4 w-4 text-red-600" /> {removendoDup ? "Removendo..." : "Remover Duplicados"}
                </Button>
                <Button variant="outline" size="sm" className="gap-1 opacity-60 cursor-not-allowed" onClick={() => toast.warning("Validador de WhatsApp pausado para evitar ban da conta. Será reativado em chip dedicado.")} data-testid="btn-verificar-whatsapp-disabled" title="Desativado temporariamente">
                  <Check className="h-4 w-4 text-gray-400" /> Verificar WhatsApp <span className="text-[10px] bg-amber-200 text-amber-900 rounded px-1 ml-1">Pausado</span>
                </Button>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowNovaContatoTag(true)} data-testid="btn-tags-contatos">
                  <Tag className="h-4 w-4" /> Tags
                </Button>
                <Button variant="outline" className="gap-1" onClick={() => setShowImportarContatos(true)} data-testid="btn-importar-contatos">
                  <Upload className="h-4 w-4" /> Importar
                </Button>
                <Button className="gap-1 bg-orange-600 hover:bg-orange-700" onClick={() => setShowNovoContato(true)} data-testid="btn-novo-contato">
                  <Plus className="h-4 w-4" /> Novo Contato
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Grupos + Tags filter */}
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant={filtroGrupo === "todos" ? "default" : "outline"} className="cursor-pointer" onClick={() => setFiltroGrupo("todos")}>
                  Todos ({(totalContatosGeral || contatosTotal).toLocaleString('pt-BR')})
                </Badge>
                {grupos.map(g => (
                  <div key={g.nome} className="inline-flex items-center gap-0.5 group" data-testid={`grupo-wrap-${g.nome}`}>
                    <Badge
                      variant={filtroGrupo === g.nome ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => setFiltroGrupo(g.nome)}
                      data-testid={`filtro-grupo-${g.nome}`}
                    >
                      {g.nome} ({g.qtd})
                    </Badge>
                    <button
                      type="button"
                      className="p-1 hover:bg-blue-50 rounded border border-blue-200 ml-0.5"
                      title="Renomear grupo"
                      onClick={(e) => { e.stopPropagation(); setEditandoGrupoDlg({ antigo: g.nome }); setGrupoEditNome(g.nome); }}
                      data-testid={`btn-edit-grupo-${g.nome}`}
                    >
                      <Pencil className="h-3 w-3 text-blue-600" />
                    </button>
                    <button
                      type="button"
                      className="p-1 hover:bg-purple-50 rounded border border-purple-200 ml-0.5"
                      title="Aplicar tag em lote no grupo"
                      onClick={(e) => { e.stopPropagation(); setAplicandoTagDlg(g.nome); setTagLoteSelecionada(""); }}
                      data-testid={`btn-tag-lote-${g.nome}`}
                    >
                      <Tag className="h-3 w-3 text-purple-600" />
                    </button>
                    <button
                      type="button"
                      className="p-1 hover:bg-orange-50 rounded border border-orange-200 ml-0.5"
                      title="Mover TODOS os contatos deste grupo para outro"
                      onClick={(e) => { e.stopPropagation(); abrirMoverTodosGrupo(g); }}
                      data-testid={`btn-mover-todos-grupo-${g.nome}`}
                    >
                      <FolderInput className="h-3 w-3 text-orange-600" />
                    </button>
                    <button
                      type="button"
                      className="p-1 hover:bg-red-50 rounded border border-red-200 ml-0.5"
                      title="Excluir grupo (mantém contatos na base geral)"
                      onClick={(e) => { e.stopPropagation(); setExcluindoGrupoDlg({ nome: g.nome, qtd: g.qtd }); }}
                      data-testid={`btn-excluir-grupo-${g.nome}`}
                    >
                      <Trash2 className="h-3 w-3 text-red-600" />
                    </button>
                  </div>
                ))}
                {contatoTags.length > 0 && (
                  <>
                    <span className="mx-1 text-gray-300">|</span>
                    {contatoTags.map(t => {
                      const count = contatoTagCounts[t.nome] || 0;
                      return (
                        <Badge
                          key={t.id}
                          variant="outline"
                          className="cursor-pointer border-2 gap-1 transition-all"
                          style={filtroContatoTag === t.nome ? { backgroundColor: t.cor, color: "#fff", borderColor: t.cor } : { borderColor: t.cor, color: t.cor }}
                          onClick={() => setFiltroContatoTag(filtroContatoTag === t.nome ? "todos" : t.nome)}
                          data-testid={`filtro-contato-tag-${t.nome}`}
                        >
                          {t.nome} ({count})
                        </Badge>
                      );
                    })}
                  </>
                )}
              </div>
              {/* Validador stats */}
              {validadorStats && (
                <div className="flex flex-wrap gap-2 mb-3 items-center">
                  <span className="text-xs text-gray-500 self-center mr-1">Tipo:</span>
                  <Badge
                    variant={filtroTipoTel === "todos" ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setFiltroTipoTel("todos")}
                    data-testid="filtro-tipo-todos"
                  >
                    Todos
                  </Badge>
                  <Badge
                    variant="outline"
                    className="cursor-pointer"
                    style={filtroTipoTel === "celular" ? { backgroundColor: "#2563eb", color: "#fff", borderColor: "#2563eb" } : { color: "#2563eb", borderColor: "#2563eb" }}
                    onClick={() => setFiltroTipoTel(filtroTipoTel === "celular" ? "todos" : "celular")}
                    data-testid="filtro-tipo-celular"
                  >
                    Celular ({contadoresGrupo.celular})
                  </Badge>
                  <Badge
                    variant="outline"
                    className="cursor-pointer"
                    style={filtroTipoTel === "fixo" ? { backgroundColor: "#64748b", color: "#fff", borderColor: "#64748b" } : { color: "#64748b", borderColor: "#64748b" }}
                    onClick={() => setFiltroTipoTel(filtroTipoTel === "fixo" ? "todos" : "fixo")}
                    data-testid="filtro-tipo-fixo"
                  >
                    Fixo ({contadoresGrupo.fixo})
                  </Badge>
                  <Badge
                    variant="outline"
                    className="cursor-pointer"
                    style={filtroTipoTel === "invalido" ? { backgroundColor: "#dc2626", color: "#fff", borderColor: "#dc2626" } : { color: "#dc2626", borderColor: "#dc2626" }}
                    onClick={() => setFiltroTipoTel(filtroTipoTel === "invalido" ? "todos" : "invalido")}
                    data-testid="filtro-tipo-invalido"
                  >
                    Inválido ({contadoresGrupo.invalido})
                  </Badge>
                  {contadoresGrupo.nao_validado > 0 && (
                    <Badge
                      variant="outline"
                      className="cursor-pointer"
                      style={filtroTipoTel === "nao_validado" ? { backgroundColor: "#a16207", color: "#fff", borderColor: "#a16207" } : { color: "#a16207", borderColor: "#a16207" }}
                      onClick={() => setFiltroTipoTel(filtroTipoTel === "nao_validado" ? "todos" : "nao_validado")}
                      data-testid="filtro-tipo-novalidado"
                    >
                      Não validado ({contadoresGrupo.nao_validado})
                    </Badge>
                  )}
                  <span className="mx-1 text-gray-300">|</span>
                  <span className="text-xs text-gray-500 self-center mr-1">WhatsApp:</span>
                  <Badge
                    variant={filtroWhatsApp === "todos" ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setFiltroWhatsApp("todos")}
                    data-testid="filtro-wpp-todos"
                  >
                    Todos
                  </Badge>
                  <Badge
                    variant="outline"
                    className="cursor-pointer"
                    style={filtroWhatsApp === "sim" ? { backgroundColor: "#16a34a", color: "#fff", borderColor: "#16a34a" } : { color: "#16a34a", borderColor: "#16a34a" }}
                    onClick={() => setFiltroWhatsApp(filtroWhatsApp === "sim" ? "todos" : "sim")}
                    data-testid="filtro-wpp-sim"
                  >
                    Com WhatsApp ({contadoresGrupo.wpp_sim})
                  </Badge>
                  <Badge
                    variant="outline"
                    className="cursor-pointer"
                    style={filtroWhatsApp === "nao" ? { backgroundColor: "#dc2626", color: "#fff", borderColor: "#dc2626" } : { color: "#dc2626", borderColor: "#dc2626" }}
                    onClick={() => setFiltroWhatsApp(filtroWhatsApp === "nao" ? "todos" : "nao")}
                    data-testid="filtro-wpp-nao"
                  >
                    Sem WhatsApp ({contadoresGrupo.wpp_nao})
                  </Badge>
                  {(validadorStats.duplicados_numeros_unicos || 0) > 0 && (
                    <Badge variant="outline" className="text-amber-600 border-amber-600" data-testid="badge-duplicados">
                      ⚠️ {validadorStats.duplicados_numeros_unicos} números duplicados
                    </Badge>
                  )}
                </div>
              )}
              {/* SMS Status filter */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-xs text-gray-500 self-center mr-1">Status SMS:</span>
                <Badge variant={filtroSmsStatus === "todos" ? "default" : "outline"} className="cursor-pointer" onClick={() => setFiltroSmsStatus("todos")} data-testid="filtro-sms-todos">
                  Todos
                </Badge>
                <Badge variant="outline" className="cursor-pointer" style={filtroSmsStatus === "enviado" ? { backgroundColor: "#16a34a", color: "#fff", borderColor: "#16a34a" } : { color: "#16a34a", borderColor: "#16a34a" }} onClick={() => setFiltroSmsStatus(filtroSmsStatus === "enviado" ? "todos" : "enviado")} data-testid="filtro-sms-enviado">
                  Enviados ({contadoresGrupo.sms_enviado})
                </Badge>
                <Badge variant="outline" className="cursor-pointer" style={filtroSmsStatus === "nao_enviado" ? { backgroundColor: "#dc2626", color: "#fff", borderColor: "#dc2626" } : { color: "#dc2626", borderColor: "#dc2626" }} onClick={() => setFiltroSmsStatus(filtroSmsStatus === "nao_enviado" ? "todos" : "nao_enviado")} data-testid="filtro-sms-nao-enviado">
                  Não Enviados ({contadoresGrupo.sms_nao_enviado})
                </Badge>
              </div>
              {/* Ação (anti-spam: filtrar contatos por ja-recebeu SMS/ligação) */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-xs text-gray-500 self-center mr-1">Ação:</span>
                <Badge variant={filtroAcao === "todos" ? "default" : "outline"} className="cursor-pointer" onClick={() => setFiltroAcao("todos")} data-testid="filtro-acao-todos">Todos</Badge>
                <Badge variant="outline" className="cursor-pointer" style={filtroAcao === "sem_acao" ? { backgroundColor: "#0f172a", color: "#fff", borderColor: "#0f172a" } : { color: "#0f172a", borderColor: "#0f172a" }} onClick={() => setFiltroAcao(filtroAcao === "sem_acao" ? "todos" : "sem_acao")} data-testid="filtro-acao-sem">
                  🆕 Sem ação ({contadoresGrupo.acao_sem})
                </Badge>
                <Badge variant="outline" className="cursor-pointer" style={filtroAcao === "so_sms" ? { backgroundColor: "#16a34a", color: "#fff", borderColor: "#16a34a" } : { color: "#16a34a", borderColor: "#16a34a" }} onClick={() => setFiltroAcao(filtroAcao === "so_sms" ? "todos" : "so_sms")} data-testid="filtro-acao-so-sms">
                  📩 Só SMS ({contadoresGrupo.acao_so_sms})
                </Badge>
                <Badge variant="outline" className="cursor-pointer" style={filtroAcao === "so_ligacao" ? { backgroundColor: "#4f46e5", color: "#fff", borderColor: "#4f46e5" } : { color: "#4f46e5", borderColor: "#4f46e5" }} onClick={() => setFiltroAcao(filtroAcao === "so_ligacao" ? "todos" : "so_ligacao")} data-testid="filtro-acao-so-lig">
                  📞 Só Ligação ({contadoresGrupo.acao_so_lig})
                </Badge>
                <Badge variant="outline" className="cursor-pointer" style={filtroAcao === "ambos" ? { backgroundColor: "#7c3aed", color: "#fff", borderColor: "#7c3aed" } : { color: "#7c3aed", borderColor: "#7c3aed" }} onClick={() => setFiltroAcao(filtroAcao === "ambos" ? "todos" : "ambos")} data-testid="filtro-acao-ambos">
                  ✅ SMS + Ligação ({contadoresGrupo.acao_ambos})
                </Badge>
              </div>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Buscar por nome ou telefone..." className="pl-9" value={searchContato} onChange={(e) => setSearchContato(e.target.value)} />
              </div>
              {selecionados.length > 0 && (
                <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200" data-testid="barra-selecao-contatos">
                  <span className="text-sm text-orange-800 font-medium">{selecionados.length.toLocaleString('pt-BR')} contato(s) selecionado(s)</span>
                  <div className="flex gap-2">
                    <Button size="sm" className="gap-1 bg-orange-600 hover:bg-orange-700" onClick={() => abrirMoverGrupo("selecionados")} data-testid="btn-mover-grupo">
                      <FolderInput className="h-4 w-4" /> Mover para Grupo
                    </Button>
                    <Button size="sm" variant="outline" onClick={limparSelecao} data-testid="btn-limpar-selecao">Limpar seleção</Button>
                  </div>
                </div>
              )}
              {contatosFiltrados.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-10 w-10 mx-auto mb-2 opacity-20" />
                  <p>Nenhum contato encontrado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={todosPaginaSelecionados} onCheckedChange={toggleSelecionarPagina} data-testid="checkbox-selecionar-todos" aria-label="Selecionar todos da página" />
                      </TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead className="text-center">Ligação</TableHead>
                      <TableHead className="text-xs">Campanha VoIP</TableHead>
                      <TableHead className="text-center">SMS</TableHead>
                      <TableHead className="text-xs">Campanha SMS</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contatosFiltrados.map(c => (
                      <TableRow key={c.id} data-testid={`contato-row-${c.id}`}>
                        <TableCell className="w-10">
                          <Checkbox checked={selecionados.includes(c.id)} onCheckedChange={() => toggleSelecionado(c.id)} data-testid={`checkbox-contato-${c.id}`} aria-label={`Selecionar ${c.nome || c.telefone}`} />
                        </TableCell>
                        <TableCell className="font-medium">
                          {c.cliente_nome ? (
                            <span className="inline-flex items-center gap-1" title="Cliente cadastrado">
                              <Users2 className="h-3 w-3 text-blue-600" /> {c.cliente_nome}
                            </span>
                          ) : (c.nome || "-")}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <span className="inline-flex items-center gap-1">
                            {c.telefone}
                            {c.whatsapp_status === true && (
                              <span title="WhatsApp validado" data-testid={`wpp-ok-${c.id}`}>
                                <MessageSquare className="h-3.5 w-3.5 text-green-600 fill-green-100" />
                              </span>
                            )}
                          </span>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{c.grupo}</Badge></TableCell>
                        <TableCell className="text-center">
                          {numerosLigados[c.telefone] ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700" title={`${numerosLigados[c.telefone].total}x ligado`} data-testid={`lig-data-${c.id}`}>
                              <Phone className="h-3 w-3" /> {(numerosLigados[c.telefone].ultima || "").split("T")[0] || ""}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {numerosLigados[c.telefone]?.campanha_nome ? (
                            <div className="flex flex-col" data-testid={`camp-voip-${c.id}`}>
                              <span className="text-indigo-700 font-medium truncate max-w-[140px]" title={numerosLigados[c.telefone].campanha_nome}>{numerosLigados[c.telefone].campanha_nome}</span>
                              <span className="text-[10px] text-gray-500">{(numerosLigados[c.telefone].ultima || "").split("T")[0]}</span>
                            </div>
                          ) : (<span className="text-gray-300">-</span>)}
                        </TableCell>
                        <TableCell className="text-center">
                          {numerosEnviados[c.telefone] ? (
                            <span className="inline-flex flex-col items-center" data-testid={`sms-status-enviado-${c.id}`}>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <Check className="h-3 w-3" /> {numerosEnviados[c.telefone].total}x
                              </span>
                              <span className="text-[10px] text-gray-500 mt-0.5">{(numerosEnviados[c.telefone].ultimo_envio || "").split("T")[0]}</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" data-testid={`sms-status-pendente-${c.id}`}>
                              Pendente
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {numerosEnviados[c.telefone]?.campanha_nome ? (
                            <div className="flex flex-col" data-testid={`camp-sms-${c.id}`}>
                              <span className="text-green-700 font-medium truncate max-w-[140px]" title={numerosEnviados[c.telefone].campanha_nome}>{numerosEnviados[c.telefone].campanha_nome}</span>
                              <span className="text-[10px] text-gray-500">{(numerosEnviados[c.telefone].ultimo_envio || "").split("T")[0]}</span>
                            </div>
                          ) : (<span className="text-gray-300">-</span>)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 items-center">
                            {(c.tags || []).map(tagNome => {
                              const tagObj = contatoTags.find(t => t.nome === tagNome);
                              return tagObj ? (
                                <span key={tagNome} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs text-white cursor-pointer hover:opacity-80" style={{ backgroundColor: tagObj.cor }} onClick={() => toggleContatoTag(c.id, tagNome)} title={`Remover "${tagNome}"`}>
                                  {tagNome} <X className="h-2.5 w-2.5" />
                                </span>
                              ) : null;
                            })}
                            <div className="relative">
                              <button
                                className={`h-6 px-2 rounded-full border text-xs transition-all flex items-center gap-1 ${tagDropdownAberto === c.id ? "bg-orange-100 border-orange-400 text-orange-600" : "border-dashed border-gray-300 text-gray-400 hover:border-orange-400 hover:text-orange-500"}`}
                                onClick={() => setTagDropdownAberto(tagDropdownAberto === c.id ? null : c.id)}
                                data-testid={`btn-add-tag-${c.id}`}
                              >
                                <Plus className="h-3 w-3" /> Tag
                              </button>
                              {tagDropdownAberto === c.id && (
                                <>
                                  <div className="fixed inset-0 z-40" onClick={() => setTagDropdownAberto(null)} />
                                  <div className="absolute left-0 top-full mt-1 z-50 bg-white border rounded-lg shadow-xl p-1.5 min-w-[160px]">
                                    {contatoTags.length === 0 ? (
                                      <p className="text-xs text-gray-400 p-2">Nenhuma tag criada. Crie uma no botão "Tags".</p>
                                    ) : contatoTags.map(t => {
                                      const isActive = (c.tags || []).includes(t.nome);
                                      return (
                                        <button key={t.id} className={`flex items-center gap-2 w-full text-left px-2.5 py-2 rounded-md text-xs transition-colors ${isActive ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"}`} onClick={(e) => { e.stopPropagation(); toggleContatoTag(c.id, t.nome); }} data-testid={`tag-option-${c.id}-${t.nome}`}>
                                          <span className="h-3.5 w-3.5 rounded-full flex-shrink-0 border border-white shadow-sm" style={{ backgroundColor: t.cor }} />
                                          <span className="flex-1">{t.nome}</span>
                                          {isActive && <Check className="h-3.5 w-3.5 text-green-600" />}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">{formatDate(c.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500" onClick={() => abrirHistorico(c.id)} title="Histórico de SMS" data-testid={`btn-historico-${c.id}`}>
                              <Clock className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluirContato(c.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {contatosTotal > CONTATOS_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-3" data-testid="contatos-paginacao">
                  <span className="text-xs text-gray-500">Mostrando {(contatosSkip + 1).toLocaleString('pt-BR')}-{Math.min(contatosSkip + CONTATOS_PAGE_SIZE, contatosTotal).toLocaleString('pt-BR')} de {contatosTotal.toLocaleString('pt-BR')}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={contatosSkip === 0 || loadingContatos} onClick={() => fetchContatos(Math.max(0, contatosSkip - CONTATOS_PAGE_SIZE))} data-testid="contatos-prev">Anterior</Button>
                    <Button size="sm" variant="outline" disabled={contatosSkip + CONTATOS_PAGE_SIZE >= contatosTotal || loadingContatos} onClick={() => fetchContatos(contatosSkip + CONTATOS_PAGE_SIZE)} data-testid="contatos-next">Próximo</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Tab: Caixa de Entrada === */}
        <TabsContent value="caixa" className="space-y-4" data-testid="tab-caixa-entrada">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Inbox className="h-5 w-5 text-teal-600" /> Caixa de Entrada</CardTitle>
                <CardDescription>SMS recebidos via DINSTAR</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowNovaTag(true)} data-testid="btn-gerenciar-tags">
                  <Tag className="h-4 w-4" /> Tags
                </Button>
                <Button variant="outline" className="gap-1" onClick={receberSms} data-testid="btn-receber-sms">
                  <RefreshCw className="h-4 w-4" /> Buscar Novas
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* View toggle + Filters row */}
              <div className="flex flex-wrap gap-2 mb-4 items-center">
                <div className="flex gap-1 mr-2 border rounded-md p-0.5">
                  <Button variant={vistaAgrupada ? "default" : "ghost"} size="sm" className="gap-1 h-7 text-xs" onClick={() => setVistaAgrupada(true)} data-testid="btn-vista-agrupada">
                    <Users2 className="h-3 w-3" /> Por Número
                  </Button>
                  <Button variant={!vistaAgrupada ? "default" : "ghost"} size="sm" className="gap-1 h-7 text-xs" onClick={() => setVistaAgrupada(false)} data-testid="btn-vista-lista">
                    <ListFilter className="h-3 w-3" /> Lista
                  </Button>
                </div>
                <Separator orientation="vertical" className="h-6" />
                <div className="flex gap-1 mr-2">
                  <Badge variant={filtroMsgTipo === "todos" ? "default" : "outline"} className="cursor-pointer" onClick={() => setFiltroMsgTipo("todos")}>Todas</Badge>
                  <Badge variant={filtroMsgTipo === "RECEBIDO" ? "default" : "outline"} className="cursor-pointer" onClick={() => setFiltroMsgTipo("RECEBIDO")}>Recebidas</Badge>
                  <Badge variant={filtroMsgTipo === "ENVIADO" ? "default" : "outline"} className="cursor-pointer" onClick={() => setFiltroMsgTipo("ENVIADO")}>Enviadas</Badge>
                </div>
                <Separator orientation="vertical" className="h-6" />
                <div className="flex gap-1 mr-2">
                  <Badge variant={filtroVerificado === "todos" ? "default" : "outline"} className="cursor-pointer bg-gray-100 text-gray-700 hover:bg-gray-200" onClick={() => setFiltroVerificado("todos")}>Todos</Badge>
                  <Badge variant={filtroVerificado === "verificado" ? "default" : "outline"} className="cursor-pointer bg-emerald-100 text-emerald-700 hover:bg-emerald-200" onClick={() => setFiltroVerificado("verificado")}>
                    <Check className="h-3 w-3 mr-1" />Verificadas {verificadasCount > 0 && <span className="ml-1 bg-emerald-600 text-white rounded-full px-1.5 text-[10px]">{verificadasCount}</span>}
                  </Badge>
                  <Badge variant={filtroVerificado === "pendente" ? "default" : "outline"} className="cursor-pointer bg-amber-100 text-amber-700 hover:bg-amber-200" onClick={() => setFiltroVerificado("pendente")}>
                    <Clock className="h-3 w-3 mr-1" />Pendentes {pendentesCount > 0 && <span className="ml-1 bg-amber-600 text-white rounded-full px-1.5 text-[10px]">{pendentesCount}</span>}
                  </Badge>
                </div>
                {smsTags.length > 0 && (
                  <>
                    <Separator orientation="vertical" className="h-6" />
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={filtroTag === "todos" ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setFiltroTag("todos")}>Sem filtro</Badge>
                      {smsTags.map(t => (
                        <Badge key={t.id} variant={filtroTag === t.nome ? "default" : "outline"} className="cursor-pointer text-xs" style={filtroTag === t.nome ? { backgroundColor: t.cor, color: "#fff" } : { borderColor: t.cor, color: t.cor }} onClick={() => setFiltroTag(filtroTag === t.nome ? "todos" : t.nome)}>
                          {t.nome} {tagCounts[t.nome] > 0 && <span className="ml-1 rounded-full px-1.5 text-[10px]" style={{ backgroundColor: "rgba(255,255,255,0.3)" }}>{tagCounts[t.nome]}</span>}
                        </Badge>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {/* Messages */}
              {mensagensFiltradas.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Inbox className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-lg font-medium">Nenhuma mensagem</p>
                  <p className="text-sm">Clique em "Buscar Novas" para verificar SMS recebidos</p>
                </div>
              ) : vistaAgrupada ? (
                /* === VISTA AGRUPADA POR NÚMERO === */
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {mensagensAgrupadas.slice(0, 50).map(grupo => {
                    const isExpanded = numeroExpandido === grupo.numero;
                    const lastRecebida = grupo.msgs.filter(m => m.tipo === "RECEBIDO").sort((a,b) => (b.created_at||"").localeCompare(a.created_at||""))[0];
                    return (
                      <div key={grupo.numero} className={`rounded-lg border transition-colors ${grupo.respondida ? "bg-blue-50/50 border-blue-200" : grupo.verificada ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-gray-200"}`} data-testid={`grupo-${grupo.numero}`}>
                        {/* Header row */}
                        <div className="p-3 cursor-pointer hover:bg-gray-50/50" onClick={() => setNumeroExpandido(isExpanded ? null : grupo.numero)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                              <span className="font-mono text-sm font-semibold">{grupo.numero}</span>
                              <Badge variant="outline" className="text-xs bg-gray-100">{grupo.totalRecebidas} recebida{grupo.totalRecebidas !== 1 ? "s" : ""}</Badge>
                              {grupo.totalEnviadas > 0 && <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">{grupo.totalEnviadas} enviada{grupo.totalEnviadas !== 1 ? "s" : ""}</Badge>}
                              {grupo.respondida && <Badge className="text-xs bg-blue-500 text-white"><Reply className="h-3 w-3 mr-1" />Respondida</Badge>}
                              {grupo.verificada && !grupo.respondida && <Badge className="text-xs bg-emerald-500 text-white"><Check className="h-3 w-3 mr-1" />Verificado</Badge>}
                              {grupo.ultimaTag && grupo.ultimaTag !== "Respondida" && (
                                <Badge className="text-xs" style={{ backgroundColor: smsTags.find(t => t.nome === grupo.ultimaTag)?.cor || "#6366f1", color: "#fff" }}>{grupo.ultimaTag}</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 mr-2">{formatDate(grupo.ultimaData)}</span>
                              {lastRecebida && !grupo.respondida && (
                                <>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-500 hover:text-blue-700" onClick={e => { e.stopPropagation(); setRespostaForm({ numero: grupo.numero, texto: "", msg_id: lastRecebida.id }); setShowResponder(true); }} title="Responder">
                                    <Reply className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-400 hover:text-red-600" onClick={e => { e.stopPropagation(); adicionarBlacklistDireto(grupo.numero); }} title="Bloquear">
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Preview of last message */}
                          {!isExpanded && lastRecebida && (
                            <p className="text-sm text-gray-500 mt-1 ml-6 truncate">{lastRecebida.texto}</p>
                          )}
                        </div>
                        {/* Expanded messages */}
                        {isExpanded && (
                          <div className="border-t px-3 pb-3 space-y-1.5 pt-2 bg-gray-50/30">
                            {grupo.msgs.map(m => (
                              <div key={m.id} className={`p-2 rounded-md text-sm ${m.tipo === "RECEBIDO" ? "bg-teal-50 border border-teal-100" : "bg-blue-50 border border-blue-100"}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {m.tipo === "RECEBIDO" ? <ArrowDownLeft className="h-3 w-3 text-teal-600" /> : <ArrowUpRight className="h-3 w-3 text-blue-600" />}
                                    <span className="text-xs font-medium text-gray-500">{m.tipo}</span>
                                    {m.verificado && <Check className="h-3 w-3 text-emerald-500" />}
                                    {m.tag && <Badge className="text-[10px] px-1.5 py-0" style={{ backgroundColor: smsTags.find(t => t.nome === m.tag)?.cor || "#6366f1", color: "#fff" }}>{m.tag}</Badge>}
                                    <span className="text-xs text-gray-400">{m.gateway}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-400">{formatDate(m.created_at)}</span>
                                    {m.tipo === "RECEBIDO" && (
                                      <>
                                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => toggleVerificado(m.id)}><CheckCircle className={`h-3 w-3 ${m.verificado ? "text-emerald-500" : "text-gray-300"}`} /></Button>
                                        <Select value={m.tag || ""} onValueChange={(v) => setTagMensagem(m.id, v === "none" ? "" : v)}>
                                          <SelectTrigger className="h-6 w-auto min-w-[65px] text-[10px] border-dashed"><Tag className="h-2.5 w-2.5 mr-0.5" /><SelectValue placeholder="Tag" /></SelectTrigger>
                                          <SelectContent>{[<SelectItem key="none" value="none">Sem tag</SelectItem>, ...smsTags.map(t => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)]}</SelectContent>
                                        </Select>
                                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-blue-500" onClick={() => { setRespostaForm({ numero: m.numero, texto: "", msg_id: m.id }); setShowResponder(true); }}><Reply className="h-3 w-3" /></Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <p className="mt-1 text-gray-700 ml-5">{m.texto}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* === VISTA LISTA (original) === */
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {mensagensFiltradas.slice(0, 50).map(m => (
                    <div key={m.id} className={`p-3 rounded-lg border transition-colors ${m.verificado ? "bg-emerald-50/50 border-emerald-200" : m.tipo === "RECEBIDO" ? "bg-teal-50 border-teal-200" : "bg-blue-50 border-blue-200"}`} data-testid={`msg-${m.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {m.tipo === "RECEBIDO" ? <ArrowDownLeft className="h-4 w-4 text-teal-600" /> : <ArrowUpRight className="h-4 w-4 text-blue-600" />}
                          <span className="font-mono text-sm font-medium">{m.numero}</span>
                          <Badge variant="outline" className={`text-xs ${m.tipo === "RECEBIDO" ? "bg-teal-100 text-teal-700" : "bg-blue-100 text-blue-700"}`}>{m.tipo}</Badge>
                          {m.verificado && <Badge className="text-xs bg-emerald-500 text-white"><Check className="h-3 w-3 mr-1" />Verificado</Badge>}
                          {m.tag && <Badge className="text-xs" style={{ backgroundColor: smsTags.find(t => t.nome === m.tag)?.cor || "#6366f1", color: "#fff" }}>{m.tag}</Badge>}
                          {m.gateway && <span className="text-xs text-gray-400">{m.gateway}</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500 mr-2">{formatDate(m.created_at)}</span>
                          {m.tipo === "RECEBIDO" && (
                            <>
                              <Button variant="ghost" size="sm" className={`h-7 px-2 text-xs ${m.verificado ? "text-emerald-600" : "text-gray-400 hover:text-emerald-600"}`} onClick={() => toggleVerificado(m.id)} title={m.verificado ? "Remover verificação" : "Marcar verificado"}>
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Select value={m.tag || ""} onValueChange={(v) => setTagMensagem(m.id, v === "none" ? "" : v)}>
                                <SelectTrigger className="h-7 w-auto min-w-[80px] text-xs border-dashed"><Tag className="h-3 w-3 mr-1" /><SelectValue placeholder="Tag" /></SelectTrigger>
                                <SelectContent><SelectItem value="none">Sem tag</SelectItem>{smsTags.map(t => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}</SelectContent>
                              </Select>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-500 hover:text-blue-700" onClick={() => { setRespostaForm({ numero: m.numero, texto: "", msg_id: m.id }); setShowResponder(true); }} title="Responder">
                                <Reply className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-400 hover:text-red-600" onClick={() => adicionarBlacklistDireto(m.numero)} title="Bloquear número">
                                <Ban className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{m.texto}</p>
                      {m.erro && <p className="text-xs text-red-500 mt-1">Erro: {m.erro}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Tab: Relatórios === */}
        <TabsContent value="relatorios" className="space-y-4" data-testid="tab-relatorios">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-slate-600" /> Relatório de SMS</CardTitle>
              <CardDescription>Estatísticas de envio e recebimento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-center">
                  <Send className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{relatorio.total_enviados}</p>
                  <p className="text-xs text-blue-600">SMS Enviados</p>
                </div>
                <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center">
                  <Inbox className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{relatorio.total_recebidos}</p>
                  <p className="text-xs text-green-600">SMS Recebidos</p>
                </div>
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-center">
                  <AlertCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{relatorio.total_erros}</p>
                  <p className="text-xs text-red-600">Erros de Envio</p>
                </div>
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                  <CheckCircle className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">{relatorio.taxa_sucesso}%</p>
                  <p className="text-xs text-emerald-600">Taxa de Sucesso</p>
                </div>
              </div>
              <Separator className="my-4" />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Megaphone className="h-4 w-4" /> Campanhas</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Total de campanhas</span><span className="font-medium">{relatorio.campanhas_total}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Campanhas enviadas</span><span className="font-medium text-green-600">{relatorio.campanhas_enviadas}</span></div>
                  </div>
                </div>
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Users className="h-4 w-4" /> Contatos</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Total de contatos</span><span className="font-medium">{relatorio.total_contatos}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Grupos</span><span className="font-medium">{grupos.length}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Blacklist</span><span className="font-medium text-red-600">{relatorio.total_blacklist || 0}</span></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Tab: Blacklist === */}
        <TabsContent value="blacklist" className="space-y-4" data-testid="tab-blacklist">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><ShieldBan className="h-5 w-5 text-red-600" /> Blacklist</CardTitle>
                <CardDescription>Números bloqueados não receberão SMS ({blacklist.length} número(s))</CardDescription>
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer inline-flex">
                  <input type="file" accept=".csv,.txt" className="hidden" onChange={importarBlacklistArquivo} disabled={uploadingBlacklist} data-testid="input-file-blacklist" />
                  <span className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium border rounded-md hover:bg-gray-50 transition-colors">
                    {uploadingBlacklist ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploadingBlacklist ? "Importando..." : "Importar Arquivo"}
                  </span>
                </label>
                <Button className="gap-1 bg-red-600 hover:bg-red-700" onClick={() => setShowNovoBlacklist(true)} data-testid="btn-novo-blacklist">
                  <Plus className="h-4 w-4" /> Adicionar Número
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Info box */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4 flex items-start gap-2">
                <Ban className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700">Números na blacklist são automaticamente excluídos de todos os envios (individual e campanhas). Você pode adicionar números individuais ou importar um arquivo .csv/.txt com um número por linha.</p>
              </div>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Buscar por telefone ou motivo..." className="pl-9" value={searchBlacklist} onChange={(e) => setSearchBlacklist(e.target.value)} data-testid="input-search-blacklist" />
              </div>
              {blacklistFiltrada.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ShieldBan className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-lg font-medium">{searchBlacklist ? "Nenhum resultado encontrado" : "Blacklist vazia"}</p>
                  <p className="text-sm">{!searchBlacklist && "Adicione números para bloquear o envio de SMS"}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-red-50">
                      <TableHead>Telefone</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Adicionado por</TableHead>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead className="text-right">Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blacklistFiltrada.slice(0, 100).map(item => (
                      <TableRow key={item.id} data-testid={`blacklist-row-${item.id}`}>
                        <TableCell className="font-mono font-medium text-red-700">{item.telefone}</TableCell>
                        <TableCell className="text-sm text-gray-600">{item.motivo || "-"}</TableCell>
                        <TableCell className="text-sm text-gray-500">{item.adicionado_por || "-"}</TableCell>
                        <TableCell className="text-sm text-gray-500">{formatDate(item.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => excluirBlacklist(item.id)} title="Remover da blacklist" data-testid={`btn-excluir-blacklist-${item.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {blacklistFiltrada.length > 100 && <p className="text-xs text-gray-400 text-center mt-2">Mostrando 100 de {blacklistFiltrada.length}</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Tab: Gestão de Chips === */}
        <TabsContent value="gestao" className="space-y-4" data-testid="tab-gestao-chips">
          {/* Summary Cards */}
          {gestaoEnvio && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Enviados no Mês</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{gestaoEnvio.total_enviados.toLocaleString()}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, (gestaoEnvio.total_enviados / gestaoEnvio.total_limite) * 100)}%` }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">de {gestaoEnvio.total_limite.toLocaleString()} limite</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Restante no Mês</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">{gestaoEnvio.total_restante.toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">SMS disponíveis no mês</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Chips Ativos</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1">{gestaoEnvio.chips_ativos}/{gestaoEnvio.chips_total}</p>
                  <p className="text-xs text-gray-400 mt-1">chips em operação</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Mês de Referência</p>
                  <p className="text-xl font-bold text-gray-700 mt-1">{gestaoEnvio.data}</p>
                  <p className="text-xs text-gray-400 mt-1">Contadores resetam todo mês</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Per Gateway Summary */}
          {gestaoEnvio && Object.entries(gestaoEnvio.por_gateway).map(([gw, data]) => (
            <Card key={gw}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Phone className="h-4 w-4" /> {gw}
                    <Badge variant="outline" className="text-xs">{data.chips_ativos} chips</Badge>
                  </CardTitle>
                  <div className="text-sm">
                    <span className="font-semibold text-blue-600">{data.enviados}</span>
                    <span className="text-gray-400"> / {data.limite}</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, data.limite > 0 ? (data.enviados / data.limite) * 100 : 0)}%` }} />
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Porta</TableHead>
                      <TableHead>Número</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Hoje</TableHead>
                      <TableHead className="text-center">Mês</TableHead>
                      <TableHead className="text-center">Limite/Mês</TableHead>
                      <TableHead className="text-center">Restante</TableHead>
                      <TableHead className="text-center">Progresso</TableHead>
                      <TableHead className="text-center">Saúde</TableHead>
                      <TableHead className="text-center">Taxa OK</TableHead>
                      <TableHead className="text-center">Último Erro</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chips.filter(c => c.gateway_nome === gw).map(chip => {
                      const pct = chip.limite_diario > 0 ? (chip.enviados_hoje / chip.limite_diario) * 100 : 0;
                      return (
                        <TableRow key={chip.id} className={!chip.ativo ? "opacity-40" : ""}>
                          <TableCell className="font-mono font-bold">P{String(chip.porta).padStart(2, '0')}</TableCell>
                          <TableCell>
                            {editandoChip === `num_${chip.id}` ? (
                              <Input defaultValue={chip.numero || ""} placeholder="Ex: 91985690867" className="w-36 h-7 text-sm font-mono" onBlur={e => { atualizarChip(chip.id, { numero: e.target.value.trim() }); setEditandoChip(null); }} onKeyDown={e => { if (e.key === "Enter") { atualizarChip(chip.id, { numero: e.target.value.trim() }); setEditandoChip(null); }}} autoFocus />
                            ) : (
                              <span className={`font-mono text-sm cursor-pointer ${chip.numero ? "font-medium text-gray-800 hover:text-blue-600" : "text-amber-500 hover:text-amber-700 font-medium"}`} onClick={() => setEditandoChip(`num_${chip.id}`)}>
                                {chip.numero || "+ Adicionar nº"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {chip.ativo ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Ativo</Badge> : <Badge variant="outline" className="text-xs text-gray-400">Inativo</Badge>}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-blue-600" title="SMS enviados hoje">
                            {chip.enviados_dia ?? 0}
                          </TableCell>
                          <TableCell className="text-center font-semibold" title="SMS enviados no mês atual">{chip.enviados_hoje}</TableCell>
                          <TableCell className="text-center">
                            {editandoChip === chip.id ? (
                              <Input type="number" defaultValue={chip.limite_diario} className="w-20 h-7 text-center text-sm" onBlur={e => atualizarChip(chip.id, { limite_diario: parseInt(e.target.value) || 700 })} onKeyDown={e => e.key === "Enter" && atualizarChip(chip.id, { limite_diario: parseInt(e.target.value) || 700 })} autoFocus data-testid={`input-limite-${chip.id}`} />
                            ) : (
                              <span className="cursor-pointer hover:text-blue-600" onClick={() => setEditandoChip(chip.id)}>{chip.limite_diario}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center font-medium text-emerald-600">{chip.restante_hoje}</TableCell>
                          <TableCell className="text-center">
                            <div className="w-full bg-gray-200 rounded-full h-2 max-w-[80px] mx-auto">
                              <div className={`h-2 rounded-full transition-all ${pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              const h = chipsHealth[chip.id];
                              if (!h) return <Badge variant="outline" className="text-xs text-gray-400">--</Badge>;
                              if (h.em_cooldown) {
                                const lvl = h.cooldown_level || 1;
                                return (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <Badge className="bg-red-100 text-red-700 text-[10px] font-bold">COOLDOWN N{lvl}</Badge>
                                    <span className="text-[9px] text-red-600">{fmtCooldown(h.cooldown_seconds_left)}</span>
                                  </div>
                                );
                              }
                              if (h.falhas_consecutivas > 0) {
                                return <Badge className="bg-amber-100 text-amber-700 text-[10px]">{h.falhas_consecutivas} falha(s)</Badge>;
                              }
                              if (h.taxa_ok !== null && h.taxa_ok < 50) {
                                return <Badge className="bg-orange-100 text-orange-700 text-[10px]">Instavel</Badge>;
                              }
                              return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Saudavel</Badge>;
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              const h = chipsHealth[chip.id];
                              if (!h || h.taxa_ok === null) return <span className="text-xs text-gray-400">--</span>;
                              const cor = h.taxa_ok >= 70 ? "text-emerald-600" : h.taxa_ok >= 40 ? "text-amber-600" : "text-red-600";
                              return (
                                <div className="flex flex-col items-center">
                                  <span className={`text-xs font-bold ${cor}`}>{h.taxa_ok.toFixed(0)}%</span>
                                  <span className="text-[9px] text-gray-400">{h.sucesso}/{h.total_envios}</span>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              const h = chipsHealth[chip.id];
                              if (!h || !h.ultimo_erro) return <span className="text-xs text-gray-300">--</span>;
                              return (
                                <span className="text-[10px] text-red-500 truncate max-w-[120px] inline-block" title={h.ultimo_erro}>
                                  {h.ultimo_erro.substring(0, 18)}{h.ultimo_erro.length > 18 ? "..." : ""}
                                </span>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {chipsHealth[chip.id]?.em_cooldown && (
                                <Button variant="outline" size="sm" className="h-7 text-xs text-emerald-600 border-emerald-300" onClick={() => recuperarChip(chip.id)} data-testid={`btn-recover-${chip.id}`}>
                                  Recuperar
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => atualizarChip(chip.id, { ativo: !chip.ativo })} data-testid={`btn-toggle-${chip.id}`}>
                                {chip.ativo ? "Desativar" : "Ativar"}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

      </Tabs>

      {/* ======= MODALS ======= */}

      {/* Modal: Config Gateway */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md" data-testid="modal-config-gateway">
          <DialogHeader>
            <DialogTitle>Configurar Gateway DINSTAR</DialogTitle>
            <DialogDescription>Configure o endereço do gateway para envio/recebimento de SMS</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>IP do Gateway *</Label><Input value={configForm.gateway_ip} onChange={(e) => setConfigForm({ ...configForm, gateway_ip: e.target.value })} placeholder="Ex: 192.168.1.100" data-testid="input-gateway-ip" /></div>
            <div><Label>Porta</Label><Input value={configForm.gateway_port} onChange={(e) => setConfigForm({ ...configForm, gateway_port: e.target.value })} placeholder="80" /></div>
            <div><Label>Porta SIM (chip)</Label><Input type="number" value={configForm.default_port} onChange={(e) => setConfigForm({ ...configForm, default_port: parseInt(e.target.value) || 1 })} placeholder="1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={salvarConfig} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-config">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Novo Template */}
      <Dialog open={showNovoTemplate} onOpenChange={setShowNovoTemplate}>
        <DialogContent className="max-w-md" data-testid="modal-novo-template">
          <DialogHeader>
            <DialogTitle>Novo Template de SMS</DialogTitle>
            <DialogDescription>Crie um modelo de mensagem reutilizável</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome *</Label><Input value={templateForm.nome} onChange={(e) => setTemplateForm({ ...templateForm, nome: e.target.value })} placeholder="Ex: FGTS Saque Aniversário" data-testid="input-template-nome" /></div>
            <div>
              <Label>Categoria</Label>
              <Select value={templateForm.categoria} onValueChange={(v) => setTemplateForm({ ...templateForm, categoria: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categoriasTemplate.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Texto da Mensagem *</Label>
              <Textarea value={templateForm.texto} onChange={(e) => setTemplateForm({ ...templateForm, texto: e.target.value })} placeholder="Digite o texto do SMS..." rows={4} data-testid="input-template-texto" />
              <p className="text-xs text-gray-400 mt-1">{templateForm.texto.length}/160 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoTemplate(false)}>Cancelar</Button>
            <Button onClick={salvarTemplate} className="bg-green-600 hover:bg-green-700" data-testid="btn-salvar-template">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Nova Campanha */}
      <Dialog open={showNovaCampanha} onOpenChange={(open) => { setShowNovaCampanha(open); if (!open) { setCampanhaOrigem("grupo"); setCampanhaArquivo(null); } }}>
        <DialogContent className="max-w-lg" data-testid="modal-nova-campanha">
          <DialogHeader>
            <DialogTitle>Nova Campanha</DialogTitle>
            <DialogDescription>Crie uma campanha de envio em massa</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome da Campanha *</Label><Input value={campanhaForm.nome} onChange={(e) => setCampanhaForm({ ...campanhaForm, nome: e.target.value })} placeholder="Ex: FGTS Março 2026" data-testid="input-campanha-nome" /></div>
            <div>
              <Label>Template (opcional)</Label>
              <Select value={campanhaForm.template_id} onValueChange={(v) => {
                setCampanhaForm({ ...campanhaForm, template_id: v });
                const tpl = templates.find(t => t.id === v);
                if (tpl && !campanhaForm.texto) setCampanhaForm(prev => ({ ...prev, template_id: v, texto: tpl.texto }));
              }}>
                <SelectTrigger><SelectValue placeholder="Selecionar template" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Nenhum</SelectItem>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Texto *</Label>
              <Textarea value={campanhaForm.texto} onChange={(e) => setCampanhaForm({ ...campanhaForm, texto: e.target.value })} rows={3} placeholder="Texto da campanha..." data-testid="input-campanha-texto" />
            </div>

            {/* Source selection */}
            <div>
              <Label className="mb-2 block">Origem dos Contatos</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={campanhaOrigem === "grupo" ? "default" : "outline"}
                  size="sm"
                  className={campanhaOrigem === "grupo" ? "bg-purple-600 hover:bg-purple-700 flex-1 gap-1" : "flex-1 gap-1"}
                  onClick={() => { setCampanhaOrigem("grupo"); setCampanhaArquivo(null); }}
                  data-testid="btn-origem-grupo"
                >
                  <Users className="h-4 w-4" /> Grupo de Contatos
                </Button>
                <Button
                  type="button"
                  variant={campanhaOrigem === "arquivo" ? "default" : "outline"}
                  size="sm"
                  className={campanhaOrigem === "arquivo" ? "bg-purple-600 hover:bg-purple-700 flex-1 gap-1" : "flex-1 gap-1"}
                  onClick={() => setCampanhaOrigem("arquivo")}
                  data-testid="btn-origem-arquivo"
                >
                  <Upload className="h-4 w-4" /> Importar Arquivo
                </Button>
              </div>
            </div>

            {campanhaOrigem === "grupo" && (
              <>
              <div>
                <Label>Grupo de Contatos</Label>
                <Select value={campanhaForm.grupo_contato} onValueChange={(v) => setCampanhaForm({ ...campanhaForm, grupo_contato: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos os contatos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">Todos</SelectItem>
                    <SelectItem value="__nao_enviados__">Não Enviados ({(contadores.sms_nao_enviado || 0).toLocaleString('pt-BR')})</SelectItem>
                    {grupos.map(g => <SelectItem key={g.nome} value={g.nome}>{g.nome} ({g.qtd})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {campanhaForm.grupo_contato && campanhaForm.grupo_contato !== "__nao_enviados__" && (
                <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-slate-700">Filtros Avançados</Label>
                    <Badge className="bg-purple-600 text-white text-xs" data-testid="preview-campanha-count">
                      {campanhaPreview.total_validos?.toLocaleString("pt-BR") || 0} de {campanhaPreview.total_no_grupo?.toLocaleString("pt-BR") || 0} contatos
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] text-gray-500">Tipo:</span>
                    {["todos","celular","fixo","invalido","nao_validado"].map(v => (
                      <Badge key={v} variant={campanhaForm.tipo_telefone === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, tipo_telefone: v})} data-testid={`camp-tipo-${v}`}>
                        {v === "todos" ? "Todos" : v === "celular" ? "Celular" : v === "fixo" ? "Fixo" : v === "invalido" ? "Inválido" : "Não validado"}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] text-gray-500">WhatsApp:</span>
                    {[["todos","Todos"],["sim","Com WhatsApp"],["nao","Sem WhatsApp"]].map(([v,l]) => (
                      <Badge key={v} variant={campanhaForm.whatsapp_status === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, whatsapp_status: v})} data-testid={`camp-wpp-${v}`}>{l}</Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] text-gray-500">Status SMS:</span>
                    {[["todos","Todos"],["enviado","Já enviado"],["nao_enviado","Não enviado"]].map(([v,l]) => (
                      <Badge key={v} variant={campanhaForm.sms_status === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, sms_status: v})} data-testid={`camp-sms-${v}`}>{l}</Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] text-gray-500">Ação:</span>
                    {[["todos","Todos"],["sem_acao","Sem ação"],["so_sms","Só SMS"],["so_ligacao","Só Ligação"],["ambos","SMS+Ligação"]].map(([v,l]) => (
                      <Badge key={v} variant={campanhaForm.acao === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, acao: v})} data-testid={`camp-acao-${v}`}>{l}</Badge>
                    ))}
                  </div>
                </div>
              )}
              </>
            )}

            {campanhaOrigem === "arquivo" && (
              <div className="space-y-3">
                <div>
                  <Label>Arquivo (CSV, Excel ou TXT) *</Label>
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt"
                    onChange={(e) => setCampanhaArquivo(e.target.files[0] || null)}
                    className="mt-1 cursor-pointer file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200"
                    data-testid="input-campanha-arquivo"
                  />
                </div>
                {campanhaArquivo && (
                  <div className="flex items-center gap-2 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                    <FileText className="h-4 w-4 text-purple-600 flex-shrink-0" />
                    <span className="text-sm text-purple-700 truncate">{campanhaArquivo.name}</span>
                    <span className="text-xs text-purple-500 flex-shrink-0">({(campanhaArquivo.size / 1024).toFixed(1)} KB)</span>
                    <button className="ml-auto text-purple-400 hover:text-red-500" onClick={() => setCampanhaArquivo(null)}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium mb-1">Formatos aceitos:</p>
                  <ul className="text-xs text-blue-600 space-y-0.5 list-disc list-inside">
                    <li>CSV ou Excel com coluna "telefone", "celular", "numero" ou "phone"</li>
                    <li>TXT com um número por linha</li>
                    <li>Números com 10 ou 11 dígitos (DDD + número)</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovaCampanha(false)}>Cancelar</Button>
            <Button onClick={salvarCampanha} className="bg-purple-600 hover:bg-purple-700 gap-1" disabled={uploadingCampanha} data-testid="btn-salvar-campanha">
              {uploadingCampanha ? <><RefreshCw className="h-4 w-4 animate-spin" /> Processando...</> : "Criar Campanha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Novo Grupo */}
      <Dialog open={showNovoGrupoDlg} onOpenChange={setShowNovoGrupoDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Criar novo grupo</DialogTitle>
            <DialogDescription>O grupo pode ser usado para filtrar e aplicar tags em lote.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Nome do grupo</Label>
            <Input value={novoGrupoNome} onChange={(e) => setNovoGrupoNome(e.target.value)} placeholder="Ex: Clientes VIP" data-testid="input-novo-grupo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoGrupoDlg(false)}>Cancelar</Button>
            <Button onClick={handleCriarGrupo} disabled={salvandoGrupo} data-testid="btn-salvar-novo-grupo">{salvandoGrupo ? "Salvando..." : "Criar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Renomear Grupo */}
      <Dialog open={!!editandoGrupoDlg} onOpenChange={(o) => { if (!o) setEditandoGrupoDlg(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Renomear grupo</DialogTitle>
            <DialogDescription>Todos os contatos do grupo "{editandoGrupoDlg?.antigo}" terão o nome do grupo atualizado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Novo nome</Label>
            <Input value={grupoEditNome} onChange={(e) => setGrupoEditNome(e.target.value)} data-testid="input-renomear-grupo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditandoGrupoDlg(null)}>Cancelar</Button>
            <Button onClick={handleRenomearGrupo} disabled={salvandoGrupo} data-testid="btn-salvar-rename-grupo">{salvandoGrupo ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Aplicar Tag em Lote */}
      <Dialog open={!!aplicandoTagDlg} onOpenChange={(o) => { if (!o) setAplicandoTagDlg(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aplicar tag em lote</DialogTitle>
            <DialogDescription>Selecione o grupo e a tag. A tag será adicionada a TODOS os contatos do grupo escolhido.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Grupo</Label>
            <Select value={aplicandoTagDlg || "todos"} onValueChange={(v) => setAplicandoTagDlg(v)}>
              <SelectTrigger data-testid="select-grupo-tag-lote">
                <SelectValue placeholder="Selecione um grupo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os grupos ({(totalContatosGeral || contatosTotal).toLocaleString('pt-BR')})</SelectItem>
                {grupos.map(g => (
                  <SelectItem key={g.nome} value={g.nome}>{g.nome} ({g.qtd})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label>Tag</Label>
            <Select value={tagLoteSelecionada} onValueChange={setTagLoteSelecionada}>
              <SelectTrigger data-testid="select-tag-lote">
                <SelectValue placeholder="Selecione uma tag" />
              </SelectTrigger>
              <SelectContent>
                {contatoTags.map(t => (
                  <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAplicandoTagDlg(null)}>Cancelar</Button>
            <Button onClick={handleAplicarTagLote} disabled={salvandoGrupo || !tagLoteSelecionada} data-testid="btn-aplicar-tag-lote">{salvandoGrupo ? "Aplicando..." : "Aplicar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Mover Contatos para Grupo */}
      <Dialog open={showMoverGrupoDlg} onOpenChange={setShowMoverGrupoDlg}>
        <DialogContent className="max-w-md" data-testid="modal-mover-grupo">
          <DialogHeader>
            <DialogTitle>Mover para Grupo</DialogTitle>
            <DialogDescription>Os contatos apenas trocam de grupo. Tags, histórico de ligações e SMS permanecem intactos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {moverEscopo === "grupo_inteiro" ? (
              <div className="px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-800" data-testid="mover-grupo-inteiro-info">
                Movendo <b>TODOS</b> os {(moverGrupoOrigem?.qtd || 0).toLocaleString('pt-BR')} contato(s) do grupo <b>"{moverGrupoOrigem?.nome}"</b>.
              </div>
            ) : (
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={moverEscopo === "selecionados" ? "default" : "outline"} className="flex-1" onClick={() => setMoverEscopo("selecionados")} data-testid="escopo-selecionados">
                  Selecionados ({selecionados.length.toLocaleString('pt-BR')})
                </Button>
                <Button type="button" size="sm" variant={moverEscopo === "todos_filtrados" ? "default" : "outline"} className="flex-1" onClick={() => setMoverEscopo("todos_filtrados")} data-testid="escopo-todos-filtrados">
                  Todos os filtrados ({(contatosTotal || 0).toLocaleString('pt-BR')})
                </Button>
              </div>
            )}
            <Label>Grupo de destino</Label>
            <Select value={grupoDestinoMover} onValueChange={setGrupoDestinoMover}>
              <SelectTrigger data-testid="select-grupo-destino">
                <SelectValue placeholder="Selecione o grupo" />
              </SelectTrigger>
              <SelectContent>
                {grupos.filter(g => moverEscopo !== "grupo_inteiro" || g.nome !== moverGrupoOrigem?.nome).map(g => (
                  <SelectItem key={g.nome} value={g.nome}>{g.nome} ({g.qtd})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoverGrupoDlg(false)}>Cancelar</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={handleMoverGrupo} disabled={movendoGrupo || !grupoDestinoMover} data-testid="btn-confirmar-mover-grupo">{movendoGrupo ? "Movendo..." : "Mover"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Excluir Grupo */}
      <Dialog open={!!excluindoGrupoDlg} onOpenChange={(o) => { if (!o) setExcluindoGrupoDlg(null); }}>
        <DialogContent className="max-w-md" data-testid="modal-excluir-grupo">
          <DialogHeader>
            <DialogTitle>Excluir grupo</DialogTitle>
            <DialogDescription>
              O grupo "{excluindoGrupoDlg?.nome}" será excluído. Os {(excluindoGrupoDlg?.qtd || 0).toLocaleString('pt-BR')} contato(s) NÃO serão apagados — eles voltam para a base geral (grupo "Geral").
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcluindoGrupoDlg(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleExcluirGrupo} disabled={excluindoGrupo} data-testid="btn-confirmar-excluir-grupo">{excluindoGrupo ? "Excluindo..." : "Excluir grupo"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Novo Contato */}
      <Dialog open={showNovoContato} onOpenChange={setShowNovoContato}>
        <DialogContent className="max-w-md" data-testid="modal-novo-contato">
          <DialogHeader>
            <DialogTitle>Novo Contato</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome</Label><Input value={contatoForm.nome} onChange={(e) => setContatoForm({ ...contatoForm, nome: e.target.value })} placeholder="Nome do contato" data-testid="input-contato-nome" /></div>
            <div><Label>Telefone *</Label><Input value={contatoForm.telefone} onChange={(e) => setContatoForm({ ...contatoForm, telefone: e.target.value })} placeholder="71999999999" data-testid="input-contato-telefone" /></div>
            <div><Label>Grupo</Label><Input value={contatoForm.grupo} onChange={(e) => setContatoForm({ ...contatoForm, grupo: e.target.value })} placeholder="Geral" /></div>
            {contatoTags.length > 0 && (
              <div>
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {contatoTags.map(t => {
                    const isSelected = (contatoForm.tags || []).includes(t.nome);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`px-3 py-1 rounded-full text-xs border-2 transition-all ${isSelected ? "text-white" : "bg-white"}`}
                        style={isSelected ? { backgroundColor: t.cor, borderColor: t.cor } : { borderColor: t.cor, color: t.cor }}
                        onClick={() => {
                          const newTags = isSelected ? (contatoForm.tags || []).filter(n => n !== t.nome) : [...(contatoForm.tags || []), t.nome];
                          setContatoForm({ ...contatoForm, tags: newTags });
                        }}
                        data-testid={`novo-contato-tag-${t.nome}`}
                      >
                        {isSelected && <Check className="h-3 w-3 inline mr-1" />}{t.nome}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoContato(false)}>Cancelar</Button>
            <Button onClick={salvarContato} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-salvar-contato">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Importar Contatos */}
      <Dialog open={showImportarContatos} onOpenChange={setShowImportarContatos}>
        <DialogContent className="max-w-md" data-testid="modal-importar-contatos">
          <DialogHeader>
            <DialogTitle>Importar Contatos</DialogTitle>
            <DialogDescription>Importe contatos da base de clientes ou de um arquivo externo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Origem</Label>
              <Select value={importForm.origem} onValueChange={(v) => setImportForm({ ...importForm, origem: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clientes">Base de Clientes</SelectItem>
                  <SelectItem value="arquivo">Arquivo Externo (CSV/Excel/TXT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grupo de Destino</Label>
              <Select
                value={(importForm.grupo && grupos.find(g => g.nome === importForm.grupo)) ? importForm.grupo : "__novo__"}
                onValueChange={(v) => {
                  if (v === "__novo__") setImportForm({ ...importForm, grupo: "" });
                  else setImportForm({ ...importForm, grupo: v });
                }}
              >
                <SelectTrigger data-testid="select-import-grupo"><SelectValue placeholder="Selecione um grupo existente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__novo__">+ Criar grupo novo</SelectItem>
                  {grupos.map(g => (<SelectItem key={g.nome} value={g.nome}>{g.nome} ({g.qtd})</SelectItem>))}
                </SelectContent>
              </Select>
              {(!importForm.grupo || !grupos.find(g => g.nome === importForm.grupo)) && (
                <Input
                  className="mt-2"
                  value={importForm.grupo}
                  onChange={(e) => setImportForm({ ...importForm, grupo: e.target.value })}
                  placeholder="Digite o nome do novo grupo"
                  data-testid="input-import-grupo-novo"
                />
              )}
              <p className="text-[10px] text-gray-500 mt-1">Selecione um grupo existente ou crie um novo. Durante a importação, números inválidos são ignorados e duplicados são detectados automaticamente.</p>
            </div>

            {/* Tag selection for import */}
            {contatoTags.length > 0 && (
              <div>
                <Label>Tags para vincular aos contatos importados</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {contatoTags.map(t => {
                    const isSelected = (importForm.tags || []).includes(t.nome);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`px-3 py-1 rounded-full text-xs border-2 transition-all ${isSelected ? "text-white" : "bg-white"}`}
                        style={isSelected ? { backgroundColor: t.cor, borderColor: t.cor } : { borderColor: t.cor, color: t.cor }}
                        onClick={() => {
                          const newTags = isSelected ? (importForm.tags || []).filter(n => n !== t.nome) : [...(importForm.tags || []), t.nome];
                          setImportForm({ ...importForm, tags: newTags });
                        }}
                        data-testid={`import-tag-${t.nome}`}
                      >
                        {isSelected && <Check className="h-3 w-3 inline mr-1" />}{t.nome}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {importForm.origem === "clientes" && (
              <>
                <div><Label>Filtrar por Tag do Cliente (opcional)</Label><Input value={importForm.tag} onChange={(e) => setImportForm({ ...importForm, tag: e.target.value })} placeholder="Ex: FGTS, Portabilidade" /></div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700">Os contatos serão importados da base de clientes. Números duplicados (já existentes na base e no arquivo) serão ignorados.</p>
                </div>
              </>
            )}

            {importForm.origem === "arquivo" && (
              <>
                <div>
                  <Label>Selecionar Arquivo *</Label>
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt"
                    onChange={(e) => { setImportForm({ ...importForm, file: e.target.files[0] || null }); setPreviewData(null); }}
                    className="mt-1 cursor-pointer file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
                    data-testid="input-importar-arquivo"
                  />
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium mb-1">Formatos aceitos:</p>
                  <ul className="text-xs text-blue-600 space-y-0.5 list-disc list-inside">
                    <li>CSV ou Excel com coluna "telefone", "celular", "numero" ou "phone"</li>
                    <li>TXT com um número por linha</li>
                  </ul>
                  <p className="text-xs text-blue-700 mt-1">Duplicatas no arquivo e na base serão ignoradas automaticamente.</p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            {previewData && importForm.origem === "arquivo" && (
              <div className="w-full mb-3 p-4 border-2 border-blue-200 bg-blue-50 rounded-lg" data-testid="preview-importar-panel">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-blue-900 text-sm">Pré-visualização do arquivo</h4>
                  <button type="button" className="text-xs text-blue-700 underline hover:text-blue-900" onClick={() => setPreviewData(null)} data-testid="btn-trocar-arquivo-preview">Trocar arquivo</button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white p-2 rounded border">
                    <p className="text-[10px] text-gray-500 uppercase">Total Linhas</p>
                    <p className="text-xl font-bold text-gray-800" data-testid="preview-total">{previewData.total_linhas}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-emerald-300">
                    <p className="text-[10px] text-emerald-600 uppercase">Celulares</p>
                    <p className="text-xl font-bold text-emerald-700" data-testid="preview-celulares">{previewData.celulares}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-blue-300">
                    <p className="text-[10px] text-blue-600 uppercase">Fixos</p>
                    <p className="text-xl font-bold text-blue-700" data-testid="preview-fixos">{previewData.fixos}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-red-300">
                    <p className="text-[10px] text-red-600 uppercase">Inválidos</p>
                    <p className="text-xl font-bold text-red-700" data-testid="preview-invalidos">{previewData.invalidos}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-amber-300">
                    <p className="text-[10px] text-amber-600 uppercase">Dup. Arquivo</p>
                    <p className="text-xl font-bold text-amber-700" data-testid="preview-dup-arquivo">{previewData.duplicados_arquivo}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-amber-300">
                    <p className="text-[10px] text-amber-600 uppercase">Já na Base</p>
                    <p className="text-xl font-bold text-amber-700" data-testid="preview-dup-base">{previewData.duplicados_base}</p>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-orange-100 border border-orange-300 rounded text-center">
                  <p className="text-xs text-orange-700 uppercase font-medium">Serão importados</p>
                  <p className="text-2xl font-bold text-orange-800" data-testid="preview-novos">{previewData.novos_para_importar} novos contatos</p>
                </div>
              </div>
            )}
            <Button variant="outline" disabled={importingContatos || loadingPreview} onClick={() => { setShowImportarContatos(false); setPreviewData(null); }} data-testid="btn-cancelar-importar">Cancelar</Button>
            {importForm.origem === "arquivo" && !previewData ? (
              <Button onClick={previewImportarArquivo} disabled={loadingPreview || !importForm.file} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-pre-visualizar-importar">
                {loadingPreview ? "Analisando..." : "Pré-visualizar"}
              </Button>
            ) : (
              <Button onClick={importarContatos} disabled={importingContatos} className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="btn-confirmar-importar">
                {importingContatos ? "Importando..." : (previewData ? `Confirmar (${previewData.novos_para_importar} novos)` : "Importar")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Editar Campanha */}
      <Dialog open={showEditarCampanha} onOpenChange={setShowEditarCampanha}>
        <DialogContent className="max-w-lg" data-testid="modal-editar-campanha">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-purple-600" /> Editar Campanha</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome da Campanha *</Label><Input value={editCampanhaForm.nome} onChange={(e) => setEditCampanhaForm({ ...editCampanhaForm, nome: e.target.value })} data-testid="edit-campanha-nome" /></div>
            <div>
              <Label>Template (opcional)</Label>
              <Select value={editCampanhaForm.template_id} onValueChange={(v) => {
                setEditCampanhaForm({ ...editCampanhaForm, template_id: v });
                const tpl = templates.find(t => t.id === v);
                if (tpl) setEditCampanhaForm(prev => ({ ...prev, template_id: v, texto: tpl.texto }));
              }}>
                <SelectTrigger><SelectValue placeholder="Selecionar template" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Nenhum</SelectItem>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Texto *</Label>
              <Textarea value={editCampanhaForm.texto} onChange={(e) => setEditCampanhaForm({ ...editCampanhaForm, texto: e.target.value })} rows={4} data-testid="edit-campanha-texto" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarCampanha(false)}>Cancelar</Button>
            <Button onClick={salvarEdicaoCampanha} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-salvar-edicao-campanha">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Relatório da Campanha - Modernizado */}
      <Dialog open={showRelatorio} onOpenChange={(open) => {
        setShowRelatorio(open);
        if (!open) { setRelatorioCampanha(null); if (relatorioAutoRefresh) { clearInterval(relatorioAutoRefresh); setRelatorioAutoRefresh(null); } }
      }}>
        <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] overflow-y-auto p-0" data-testid="modal-relatorio-sms">
          {/* Header escuro */}
          <div className="bg-gradient-to-r from-indigo-800 to-indigo-700 text-white p-5 rounded-t-lg">
            <div className="flex justify-between items-start">
              <div>
                <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" /> {relatorioCampanha?.campanha?.nome || "Campanha"}
                </DialogTitle>
                <DialogDescription className="text-indigo-200 text-xs mt-1">
                  Template: {relatorioCampanha?.campanha?.template_id || "-"} | Grupo: {relatorioCampanha?.campanha?.grupo_contato || "-"}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-600/50 rounded text-[10px] text-indigo-100">
                  <Clock className="h-3 w-3" />
                  <span>{horarioConfig.hora_inicio} - {horarioConfig.hora_fim}</span>
                </div>
                <Badge className={relatorioCampanha?.campanha?.status === "ENVIANDO" ? "bg-green-500 text-white animate-pulse" : relatorioCampanha?.campanha?.status === "FINALIZADA" ? "bg-slate-500 text-white" : "bg-yellow-500 text-white"}>
                  {relatorioCampanha?.campanha?.status || "-"}
                </Badge>
              </div>
            </div>
            {relatorioCampanha?.resumo && (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-indigo-200 mb-1">
                  <span>{(relatorioCampanha.resumo.processados || 0).toLocaleString()} de {(relatorioCampanha.resumo.total || 0).toLocaleString()}</span>
                  <span>{relatorioCampanha.resumo.progresso || 0}%</span>
                </div>
                <div className="w-full bg-indigo-600 rounded-full h-2.5">
                  <div className="bg-gradient-to-r from-green-400 to-emerald-400 h-2.5 rounded-full transition-all duration-500" style={{ width: `${relatorioCampanha?.resumo?.progresso || 0}%` }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-5 space-y-5">
            {loadingRelatorio ? (
              <div className="text-center py-8"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" /><p className="text-sm text-gray-500 mt-2">Carregando...</p></div>
            ) : relatorioCampanha && (() => {
              const rs = relatorioCampanha.resumo || {};
              const totalPages = Math.ceil((relatorioCampanha.total_filtrados || 0) / 50);
              return (<>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { label: "Total", val: rs.total, bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700" },
                    { label: "Enviados", val: rs.enviados_ok, bg: "bg-green-50", border: "border-green-200", text: "text-green-700" },
                    { label: "Erros", val: rs.enviados_erro, bg: "bg-red-50", border: "border-red-200", text: "text-red-700" },
                    { label: "Pendentes", val: rs.pendentes, bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700" },
                    { label: "Taxa Sucesso", val: `${rs.taxa_sucesso || 0}%`, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
                  ].map((item, i) => (
                    <div key={i} className={`text-center p-3 rounded-lg border ${item.bg} ${item.border}`}>
                      <p className={`text-xl font-bold ${item.text}`}>{typeof item.val === "number" ? (item.val || 0).toLocaleString() : item.val}</p>
                      <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">{item.label}</p>
                    </div>
                  ))}
                </div>

                {/* DLR - Entrega Real (Delivery Report) - Oculto: chipeira atual nao suporta. Endpoints prontos para futura troca. */}
                {false && rs.dlr && (
                  <div className="rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wider">DLR</span>
                        <span className="text-xs font-semibold text-gray-700">Entrega Real na Operadora</span>
                      </div>
                      <span className="text-[10px] text-gray-500" title="Delivery Report: status retornado pela operadora">Status real do destinatario</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <div className="text-center p-2 rounded border bg-emerald-50 border-emerald-200">
                        <p className="text-lg font-bold text-emerald-700">{(rs.dlr.entregues || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-gray-500 font-medium uppercase">Entregues</p>
                      </div>
                      <div className="text-center p-2 rounded border bg-red-50 border-red-200">
                        <p className="text-lg font-bold text-red-700">{(rs.dlr.falhou || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-gray-500 font-medium uppercase">Falhou</p>
                      </div>
                      <div className="text-center p-2 rounded border bg-amber-50 border-amber-200">
                        <p className="text-lg font-bold text-amber-700">{(rs.dlr.expirado || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-gray-500 font-medium uppercase">Expirado</p>
                      </div>
                      <div className="text-center p-2 rounded border bg-orange-50 border-orange-200">
                        <p className="text-lg font-bold text-orange-700">{(rs.dlr.rejeitado || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-gray-500 font-medium uppercase">Rejeitado</p>
                      </div>
                      <div className="text-center p-2 rounded border bg-gray-50 border-gray-200">
                        <p className="text-lg font-bold text-gray-600">{(rs.dlr.pendente_dlr || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-gray-500 font-medium uppercase">Aguardando DLR</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-600">Taxa de entrega real:</span>
                        <span className={`text-sm font-bold ${(rs.dlr.taxa_entrega || 0) >= 80 ? "text-emerald-700" : (rs.dlr.taxa_entrega || 0) >= 50 ? "text-amber-700" : "text-red-700"}`}>
                          {rs.dlr.taxa_entrega || 0}%
                        </span>
                      </div>
                      {((rs.dlr.falhou || 0) + (rs.dlr.expirado || 0) + (rs.dlr.rejeitado || 0)) > 0 && (
                        <button
                          onClick={async () => {
                            try {
                              const r = await axios.get(`${API}/sms/campanhas/${relatorioCampanha.campanha.id}/falhas?limit=5000`);
                              const csv = ["numero,status_real,motivo_falha,chip,data_dlr"].concat(
                                (r.data.registros || []).map(x => `${x.numero},${x.status_real || ""},"${(x.motivo_falha || "").replace(/"/g, "'")}",${x.chip || ""},${x.dlr_recebido_em || ""}`)
                              ).join("\n");
                              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url; a.download = `falhas_${relatorioCampanha.campanha.id}.csv`;
                              document.body.appendChild(a); a.click(); document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                              toast.success(`${r.data.total} falhas exportadas`);
                            } catch (e) { toast.error("Erro ao exportar falhas"); }
                          }}
                          className="text-[10px] px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 font-medium"
                          data-testid="btn-exportar-falhas-csv">
                          Exportar falhas (CSV)
                        </button>
                      )}
                    </div>
                    {((rs.dlr.entregues || 0) + (rs.dlr.falhou || 0) + (rs.dlr.expirado || 0) + (rs.dlr.rejeitado || 0)) === 0 && (rs.enviados_ok || 0) > 0 && (
                      <p className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">
                        Nenhum DLR recebido ainda. Configure o webhook na chipeira apontando para <code className="bg-white px-1 rounded text-[9px]">https://stilocred.com/api/sms/dlr</code> para receber status reais.
                      </p>
                    )}
                  </div>
                )}

                {/* Chips + Gateways + Actions */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-[10px] text-gray-500 font-medium uppercase mb-1.5">Chips Utilizados</p>
                    {Object.entries(rs.chips || {}).sort((a,b) => b[1]-a[1]).slice(0,8).map(([chip, cnt]) => (
                      <div key={chip} className="flex justify-between text-xs mb-0.5">
                        <span className="text-gray-600 truncate max-w-[120px]">{chip}</span>
                        <span className="font-mono font-semibold">{cnt.toLocaleString()}</span>
                      </div>
                    ))}
                    {Object.keys(rs.chips || {}).length === 0 && <p className="text-xs text-gray-400">-</p>}
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-[10px] text-gray-500 font-medium uppercase mb-1.5">Gateways</p>
                    {Object.entries(rs.gateways || {}).map(([gw, cnt]) => (
                      <div key={gw} className="flex justify-between text-xs mb-0.5">
                        <span className="text-gray-600">{gw}</span>
                        <span className="font-mono font-semibold">{cnt.toLocaleString()}</span>
                      </div>
                    ))}
                    {Object.keys(rs.gateways || {}).length === 0 && <p className="text-xs text-gray-400">-</p>}
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex flex-col gap-2">
                    <p className="text-[10px] text-gray-500 font-medium uppercase mb-0.5">Acoes</p>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-green-300 text-green-700 hover:bg-green-50 w-full" onClick={() => exportarRelatorioExcel(relatorioCampanha.campanha.id)} data-testid="btn-exportar-excel">
                      <Download className="h-3.5 w-3.5" /> Exportar Excel
                    </Button>
                    {rs.enviados_erro > 0 && relatorioCampanha.campanha.status !== "ENVIANDO" && (
                      <Button size="sm" className="h-8 text-xs gap-1.5 bg-orange-600 hover:bg-orange-700 w-full" onClick={() => reenviarCampanha(relatorioCampanha.campanha.id, "erros")} data-testid="btn-reenviar-erros">
                        <RotateCcw className="h-3.5 w-3.5" /> Reenviar Erros ({rs.enviados_erro})
                      </Button>
                    )}
                    {relatorioCampanha.campanha.status !== "ENVIANDO" && (
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50 w-full" onClick={() => reenviarCampanha(relatorioCampanha.campanha.id, "todos")} data-testid="btn-reenviar-todos">
                        <RotateCcw className="h-3.5 w-3.5" /> Reenviar Todos
                      </Button>
                    )}
                    {(relatorioCampanha.campanha.status === "PAUSADA" || relatorioCampanha.campanha.status === "ERRO_PARCIAL" || relatorioCampanha.campanha.status === "ERRO") && rs.pendentes > 0 && (
                      <Button size="sm" className="h-8 text-xs gap-1.5 bg-blue-600 hover:bg-blue-700 w-full" onClick={() => retomarCampanha(relatorioCampanha.campanha.id)} data-testid="btn-retomar-relatorio">
                        <Play className="h-3.5 w-3.5" /> Iniciar de Onde Parou ({rs.pendentes.toLocaleString()} pendentes)
                      </Button>
                    )}
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex flex-wrap gap-1.5 items-center">
                  <span className="text-xs text-gray-500 mr-1">Filtrar:</span>
                  {["TODOS","ENVIADO","ERRO","PENDENTE"].map(f => (
                    <button key={f} onClick={() => {
                      setLoadingRelatorio(true);
                      axios.get(`${API}/sms/campanhas/${relatorioCampanha.campanha.id}/relatorio?status_filter=${f}&page=1&per_page=50`)
                        .then(r => setRelatorioCampanha(r.data))
                        .finally(() => setLoadingRelatorio(false));
                    }}
                      className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition-all ${(!relatorioCampanha._filter && f === "TODOS") || relatorioCampanha._filter === f ? "bg-indigo-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                      {f === "TODOS" ? "Todos" : f === "ENVIADO" ? `Enviados (${rs.enviados_ok || 0})` : f === "ERRO" ? `Erros (${rs.enviados_erro || 0})` : `Pendentes (${rs.pendentes || 0})`}
                    </button>
                  ))}
                </div>

                {/* Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[45vh] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-50 z-10">
                        <TableRow>
                          <TableHead className="text-xs font-semibold">Numero</TableHead>
                          <TableHead className="text-xs font-semibold">Nome</TableHead>
                          <TableHead className="text-xs font-semibold">Status</TableHead>
                          <TableHead className="text-xs font-semibold">Chip</TableHead>
                          <TableHead className="text-xs font-semibold">Gateway</TableHead>
                          <TableHead className="text-xs font-semibold">Data/Hora</TableHead>
                          <TableHead className="text-xs font-semibold">Erro</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(relatorioCampanha.registros || []).length === 0 ? (
                          <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-6 text-sm">Nenhum registro neste filtro</TableCell></TableRow>
                        ) : (relatorioCampanha.registros || []).map((r, idx) => (
                          <TableRow key={idx} className="hover:bg-slate-50/50">
                            <TableCell className="font-mono text-xs py-1.5">{r.numero}</TableCell>
                            <TableCell className="text-xs py-1.5 max-w-[120px] truncate">{r.nome || "-"}</TableCell>
                            <TableCell className="py-1.5">
                              <Badge className={`text-[10px] px-1.5 py-0 border ${r.status === "ENVIADO" ? "bg-green-100 text-green-700 border-green-200" : r.status === "ERRO" ? "bg-red-100 text-red-700 border-red-200" : "bg-yellow-100 text-yellow-700 border-yellow-200"}`}>
                                {r.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-[10px] text-gray-500 py-1.5">{r.chip || "-"}</TableCell>
                            <TableCell className="text-[10px] text-gray-500 py-1.5">{r.gateway || "-"}</TableCell>
                            <TableCell className="text-xs text-gray-500 py-1.5">{r.data_envio ? formatDate(r.data_envio) : "-"}</TableCell>
                            <TableCell className="text-[10px] text-red-500 py-1.5 max-w-[150px] truncate">{r.erro || ""}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t text-xs">
                      <span className="text-gray-500">{(relatorioCampanha.total_filtrados || 0).toLocaleString()} registros</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => {
                          const pg = Math.max(1, (relatorioCampanha.page || 1) - 1);
                          setLoadingRelatorio(true);
                          axios.get(`${API}/sms/campanhas/${relatorioCampanha.campanha.id}/relatorio?page=${pg}&per_page=50`)
                            .then(r => setRelatorioCampanha(r.data)).finally(() => setLoadingRelatorio(false));
                        }} disabled={(relatorioCampanha.page || 1) <= 1} className="px-2 py-0.5 rounded bg-white border disabled:opacity-30 hover:bg-gray-50">Ant</button>
                        <span className="px-2 text-gray-600">{relatorioCampanha.page || 1} / {totalPages}</span>
                        <button onClick={() => {
                          const pg = Math.min(totalPages, (relatorioCampanha.page || 1) + 1);
                          setLoadingRelatorio(true);
                          axios.get(`${API}/sms/campanhas/${relatorioCampanha.campanha.id}/relatorio?page=${pg}&per_page=50`)
                            .then(r => setRelatorioCampanha(r.data)).finally(() => setLoadingRelatorio(false));
                        }} disabled={(relatorioCampanha.page || 1) >= totalPages} className="px-2 py-0.5 rounded bg-white border disabled:opacity-30 hover:bg-gray-50">Prox</button>
                      </div>
                    </div>
                  )}
                </div>
              </>);
            })()}
          </div>
        </DialogContent>
      </Dialog>

            {/* Modal: Histórico SMS do Contato */}
      <Dialog open={showHistorico} onOpenChange={(open) => { setShowHistorico(open); if (!open) setHistoricoContato(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="modal-historico-contato">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-blue-600" /> Histórico de SMS</DialogTitle>
            {historicoContato?.contato && (
              <DialogDescription>
                {historicoContato.contato.nome || "Sem nome"} - {historicoContato.contato.telefone} | 
                <span className="text-green-600 font-medium ml-1">{historicoContato.total_enviados} enviado(s)</span> · 
                <span className="text-blue-600 font-medium ml-1">{historicoContato.total_recebidos} recebido(s)</span>
                {historicoContato.campanhas_enviadas?.length > 0 && (
                  <span className="text-purple-600 font-medium ml-1"> · {historicoContato.campanhas_enviadas.length} campanha(s)</span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="overflow-y-auto max-h-[55vh] pr-1">
            {loadingHistorico ? (
              <div className="text-center py-8"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" /><p className="text-sm text-gray-500 mt-2">Carregando...</p></div>
            ) : !historicoContato?.historico?.length ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">Nenhum SMS encontrado para este contato</p>
              </div>
            ) : (
              <div className="space-y-0">
                {historicoContato.historico.map((msg, idx) => (
                  <div key={msg.id || idx} className={`flex gap-3 py-3 ${idx > 0 ? "border-t" : ""}`} data-testid={`historico-msg-${idx}`}>
                    <div className={`flex-shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center ${msg.tipo === "ENVIADO" ? "bg-green-100" : "bg-blue-100"}`}>
                      {msg.tipo === "ENVIADO" ? <ArrowUpRight className="h-4 w-4 text-green-600" /> : <ArrowDownLeft className="h-4 w-4 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-xs ${msg.tipo === "ENVIADO" ? "border-green-300 text-green-700" : "border-blue-300 text-blue-700"}`}>
                          {msg.tipo === "ENVIADO" ? "Enviado" : "Recebido"}
                        </Badge>
                        {msg.campanha_nome && (
                          <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                            <Megaphone className="h-3 w-3 mr-1" />{msg.campanha_nome}
                          </Badge>
                        )}
                        {msg.status && msg.status !== "ENVIADO" && (
                          <Badge variant="outline" className={`text-xs ${msg.status === "ERRO" ? "border-red-300 text-red-600" : "border-gray-300"}`}>
                            {msg.status}
                          </Badge>
                        )}
                        <span className="text-xs text-gray-400 ml-auto">{formatDate(msg.created_at)}</span>
                      </div>
                      {msg.texto && <p className="text-sm text-gray-700 mt-1 line-clamp-2">{msg.texto}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {historicoContato?.campanhas_enviadas?.length > 0 && (
            <div className="border-t pt-3 mt-2">
              <p className="text-xs text-gray-500 font-medium mb-2">Campanhas já enviadas para este contato:</p>
              <div className="flex flex-wrap gap-1">
                {historicoContato.campanhas_enviadas.map((nome, i) => (
                  <Badge key={i} className="text-xs bg-purple-50 text-purple-700 border border-purple-200">
                    <Megaphone className="h-3 w-3 mr-1" />{nome}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Adicionar Blacklist */}
      <Dialog open={showNovoBlacklist} onOpenChange={setShowNovoBlacklist}>
        <DialogContent className="max-w-md" data-testid="modal-novo-blacklist">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldBan className="h-5 w-5 text-red-600" /> Adicionar à Blacklist</DialogTitle>
            <DialogDescription>O número não receberá mais SMS</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Telefone *</Label><Input value={blacklistForm.telefone} onChange={(e) => setBlacklistForm({ ...blacklistForm, telefone: e.target.value })} placeholder="71999999999" data-testid="input-blacklist-telefone" /></div>
            <div><Label>Motivo (opcional)</Label><Input value={blacklistForm.motivo} onChange={(e) => setBlacklistForm({ ...blacklistForm, motivo: e.target.value })} placeholder="Ex: Solicitou remoção, Número inválido" data-testid="input-blacklist-motivo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoBlacklist(false)}>Cancelar</Button>
            <Button onClick={adicionarBlacklist} className="bg-red-600 hover:bg-red-700" data-testid="btn-salvar-blacklist">Bloquear Número</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Responder SMS */}
      <Dialog open={showResponder} onOpenChange={setShowResponder}>
        <DialogContent className="max-w-md" data-testid="modal-responder-sms">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Reply className="h-5 w-5 text-blue-600" /> Responder SMS</DialogTitle>
            <DialogDescription>Enviar resposta para {respostaForm.numero}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Número</Label>
              <Input value={respostaForm.numero} disabled className="font-mono bg-gray-50" data-testid="resposta-numero" />
            </div>
            <div>
              <Label>Mensagem</Label>
              <Textarea value={respostaForm.texto} onChange={e => setRespostaForm(f => ({ ...f, texto: e.target.value }))} placeholder="Digite sua resposta..." rows={3} data-testid="resposta-texto" />
              <p className="text-xs text-gray-400 mt-1">{respostaForm.texto.length}/300 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponder(false)}>Cancelar</Button>
            <Button onClick={responderSms} disabled={respondendo || !respostaForm.texto.trim()} className="gap-1" data-testid="btn-enviar-resposta">
              <Send className="h-4 w-4" /> {respondendo ? "Enviando..." : "Enviar Resposta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gerenciar Tags de Contatos */}
      <Dialog open={showNovaContatoTag} onOpenChange={setShowNovaContatoTag}>
        <DialogContent className="max-w-md" data-testid="modal-gerenciar-contato-tags">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Tag className="h-5 w-5 text-orange-600" /> Tags de Contatos</DialogTitle>
            <DialogDescription>Crie e gerencie tags exclusivas para contatos</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Tags existentes</Label>
              {contatoTags.length === 0 ? <p className="text-sm text-gray-400">Nenhuma tag de contato criada</p> : (
                <div className="flex flex-wrap gap-2">
                  {contatoTags.map(t => (
                    <div key={t.id} className="flex items-center gap-1 px-2 py-1 rounded-full text-sm text-white" style={{ backgroundColor: t.cor }}>
                      {t.nome}
                      <button className="ml-1 hover:bg-white/20 rounded-full p-0.5" onClick={() => excluirContatoTag(t.id)} data-testid={`btn-excluir-contato-tag-${t.id}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Separator />
            <div className="space-y-2">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Nova tag de contato</Label>
              <div className="flex gap-2">
                <Input placeholder="Nome da tag" value={novaContatoTagForm.nome} onChange={e => setNovaContatoTagForm(f => ({ ...f, nome: e.target.value }))} className="flex-1" data-testid="nova-contato-tag-nome" />
                <input type="color" value={novaContatoTagForm.cor} onChange={e => setNovaContatoTagForm(f => ({ ...f, cor: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border" data-testid="nova-contato-tag-cor" />
                <Button onClick={criarContatoTag} size="sm" className="gap-1" data-testid="btn-criar-contato-tag">
                  <Plus className="h-4 w-4" /> Criar
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gerenciar Tags (Caixa de Entrada) */}
      <Dialog open={showNovaTag} onOpenChange={setShowNovaTag}>
        <DialogContent className="max-w-md" data-testid="modal-gerenciar-tags">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Tag className="h-5 w-5 text-indigo-600" /> Tags da Caixa de Entrada</DialogTitle>
            <DialogDescription>Crie e gerencie tags para organizar mensagens da caixa de entrada</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Existing tags */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Tags existentes</Label>
              {smsTags.length === 0 ? <p className="text-sm text-gray-400">Nenhuma tag criada</p> : (
                <div className="flex flex-wrap gap-2">
                  {smsTags.map(t => (
                    <div key={t.id} className="flex items-center gap-1 px-2 py-1 rounded-full text-sm text-white" style={{ backgroundColor: t.cor }}>
                      {t.nome}
                      <button className="ml-1 hover:bg-white/20 rounded-full p-0.5" onClick={() => excluirTag(t.id)} data-testid={`btn-excluir-tag-${t.id}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Separator />
            {/* Create new tag */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">Nova tag</Label>
              <div className="flex gap-2">
                <Input placeholder="Nome da tag" value={novaTagForm.nome} onChange={e => setNovaTagForm(f => ({ ...f, nome: e.target.value }))} className="flex-1" data-testid="nova-tag-nome" />
                <input type="color" value={novaTagForm.cor} onChange={e => setNovaTagForm(f => ({ ...f, cor: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border" data-testid="nova-tag-cor" />
                <Button onClick={criarTag} size="sm" className="gap-1" data-testid="btn-criar-tag">
                  <Plus className="h-4 w-4" /> Criar
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
